import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import MpPixDialog from "@/components/MpPixDialog";
import { Camera, MessageCircle, MoreVertical, ClipboardEdit, Building2, CheckCircle2, XCircle, RefreshCw, Trash2, Activity, Dumbbell, DollarSign, FileText, Handshake, ClipboardList, HeartPulse, Wrench, X, VideoOff, Ban, KeyRound, MapPin, Phone, User, Users, Save, ShieldOff, ShieldCheck, Plus, Sparkles, Loader2, ChevronDown, ChevronRight, Pencil, Copy, Printer, CalendarPlus, CalendarMinus, Percent, Gift, Video, Play, Upload, Youtube, Search as SearchIcon, ArrowRightLeft, PauseCircle, PlayCircle, HeartHandshake, QrCode } from "lucide-react";
import { loadFaceModels, detectEnrollFace } from "@/lib/faceapi";
import { maskCPF, maskPhone, maskCEP, maskDate, isoFromBRDate, brDateFromISO, lookupCEP, unmask, endOfValidity } from "@/lib/masks";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SellStartDate } from "@/components/SellStartDate";
import StudentPresencas from "@/components/StudentPresencas";
import { CalendarDays } from "lucide-react";

const TABS = [
  { key: "resumo", label: "Resumo", icon: FileText },
  { key: "comunicacao", label: "Comunicação", icon: MessageCircle },
  { key: "vendas", label: "Vendas", icon: Handshake },
  { key: "contratos", label: "Contratos", icon: ClipboardList },
  { key: "financeiro", label: "Financeiro", icon: DollarSign },
  { key: "treinos", label: "Treinos", icon: Dumbbell },
];

const METHOD_LABEL = { cash: "Dinheiro", card: "Cartão", pix: "Pix", pix_maquineta: "Pix Maquineta", pix_qrcode: "Pix QR Code", manual: "Manual", stripe: "Stripe", nextfit: "Next Fit", transferencia: "Transferência/Depósito" };
const METHOD_COLOR = { cash: "#10B981", card: "#3B82F6", pix: "#E11D48", pix_maquineta: "#E11D48", pix_qrcode: "#E11D48", manual: "#F59E0B", stripe: "#6366F1", nextfit: "#0EA5E9", transferencia: "#0D9488" };
const STATUS_LABEL = { paid: "Pago", pending: "Pendente", canceled: "Cancelado", refunded: "Estornado", failed: "Falhou" };
const SOURCE_LABEL = {
  "sistema": "Sistema",
  "admin_qr": "Venda Rápida",
  "kiosk": "Totem",
  "portal": "Portal do Aluno",
  "portal_card": "Portal do Aluno",
  "portal_pix": "Portal do Aluno",
  "self-signup": "Site de Vendas",
  "paylink": "Link de Cobrança",
  "mercadopago": "Mercado Pago",
  "nextfit": "Histórico Next Fit",
};
const SOURCE_COLOR = {
  "sistema": "#64748b",
  "admin_qr": "#64748b",
  "kiosk": "#8B5CF6",
  "portal": "#06B6D4",
  "portal_card": "#06B6D4",
  "portal_pix": "#06B6D4",
  "self-signup": "#E11D48",
  "paylink": "#F59E0B",
  "mercadopago": "#009ee3",
};

function calcAge(birthdate) {
  if (!birthdate) return null;
  try {
    const b = new Date(birthdate);
    const t = new Date();
    let a = t.getFullYear() - b.getFullYear();
    const m = t.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && t.getDate() < b.getDate())) a--;
    return a;
  } catch { return null; }
}

function fmtDate(iso) {
  if (!iso) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso.split("-").reverse().join("/");
  try { return new Date(iso).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" }); } catch { return "—"; }
}

export default function StudentDetail({ studentId, open, onClose, onUpdate }) {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";
  const canPresencas = isAdmin || (currentUser?.permissions || []).includes("attendance.view");
  const tabs = canPresencas ? [...TABS, { key: "presencas", label: "Presenças", icon: CalendarDays }] : TABS;
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [tab, setTab] = useState("resumo");
  const [renewMethod, setRenewMethod] = useState("cash");
  const [mpSellOpen, setMpSellOpen] = useState(false);
  const [mpRenewOpen, setMpRenewOpen] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [payPage, setPayPage] = useState(null);
  const loadPayments = async (page = 1) => {
    try { const { data: pp } = await api.get(`/students/${studentId}/payments?page=${page}&page_size=50`); setPayPage(pp); }
    catch (e) { toast.error("Erro ao carregar histórico financeiro"); }
  };
  useEffect(() => { if (open && studentId && tab === "financeiro" && data) loadPayments(payPage?.page || 1); /* eslint-disable-next-line */ }, [tab, studentId, data]);
  const [sellPlan, setSellPlan] = useState("");
  const [sellMethod, setSellMethod] = useState("cash");
  const [sellDiscount, setSellDiscount] = useState(0);
  const [sellSplit, setSellSplit] = useState(false);
  const [sellSplitMethod, setSellSplitMethod] = useState("pix");
  const [sellSplitAmount, setSellSplitAmount] = useState("");
  const [selling, setSelling] = useState(false);
  const [sellStartCustom, setSellStartCustom] = useState(false);
  const [sellStartDate, setSellStartDate] = useState("");
  const [addDaysOpen, setAddDaysOpen] = useState(false);
  const [addDaysForm, setAddDaysForm] = useState({ days: 7, reason: "" });
  const [addDaysSaving, setAddDaysSaving] = useState(false);
  const [rmDaysOpen, setRmDaysOpen] = useState(false);
  const [rmDaysForm, setRmDaysForm] = useState({ days: 1, reason: "" });
  const [rmDaysSaving, setRmDaysSaving] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferForm, setTransferForm] = useState({ target_student_id: "", reason: "" });
  const [transferSaving, setTransferSaving] = useState(false);
  const [transferSearch, setTransferSearch] = useState("");
  const [allStudents, setAllStudents] = useState([]);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendForm, setSuspendForm] = useState({ reason: "", category: "saude" });
  const [suspendSaving, setSuspendSaving] = useState(false);
  const [unitsList, setUnitsList] = useState([]);
  const [unitOpen, setUnitOpen] = useState(false);
  const [unitId, setUnitId] = useState("");
  const [unitSaving, setUnitSaving] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [waMsg, setWaMsg] = useState("");
  const [delOpen, setDelOpen] = useState(false);
  const [delTx, setDelTx] = useState(null);
  const [delReason, setDelReason] = useState("");
  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoStreaming, setPhotoStreaming] = useState(false);
  const [photoSaving, setPhotoSaving] = useState(false);
  const [modelsReady, setModelsReady] = useState(false);
  const [cadastroOpen, setCadastroOpen] = useState(false);
  const [cadastroForm, setCadastroForm] = useState({});
  const [cadastroSaving, setCadastroSaving] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [newPwd, setNewPwd] = useState("");
  const [cepLoading, setCepLoading] = useState(false);
  const [wkManualOpen, setWkManualOpen] = useState(false);
  const [wkManualForm, setWkManualForm] = useState(null);
  const [wkEditingId, setWkEditingId] = useState(null);
  const [wkAIOpen, setWkAIOpen] = useState(false);
  const [wkAIForm, setWkAIForm] = useState({ goal: "", days_per_week: 3, focus_areas: "", notes: "" });
  const [wkGenerating, setWkGenerating] = useState(false);
  const [wkSaving, setWkSaving] = useState(false);
  const [wkExpanded, setWkExpanded] = useState({});
  const [wkDupOpen, setWkDupOpen] = useState(false);
  const [wkDupWorkout, setWkDupWorkout] = useState(null);
  const [wkDupTargets, setWkDupTargets] = useState([]);
  const [wkDupSearch, setWkDupSearch] = useState("");
  const [wkDupSaving, setWkDupSaving] = useState(false);
  const [videoModal, setVideoModal] = useState(null); // { url, title }
  const [wkCopyOpen, setWkCopyOpen] = useState(false);
  const [wkCopyTab, setWkCopyTab] = useState("students"); // "students" | "templates"
  const [wkCopyList, setWkCopyList] = useState([]);
  const [wkCopyTemplates, setWkCopyTemplates] = useState([]);
  const [wkCopySearch, setWkCopySearch] = useState("");
  const [wkCopyCat, setWkCopyCat] = useState("Todas");
  const [wkCopyLoading, setWkCopyLoading] = useState(false);
  const [wkCopyStudentsMap, setWkCopyStudentsMap] = useState({});
  const [wkTplSaving, setWkTplSaving] = useState({});
  const [videoPickerOpen, setVideoPickerOpen] = useState(false);
  const [videoPickerTarget, setVideoPickerTarget] = useState(null); // {di, ei}
  const [videoTab, setVideoTab] = useState("library");
  const [library, setLibrary] = useState([]);
  const [librarySearch, setLibrarySearch] = useState("");
  const [ytUrl, setYtUrl] = useState("");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const load = async () => {
    if (!studentId) return;
    const [d, p, u] = await Promise.all([
      api.get(`/students/${studentId}/full`),
      api.get("/plans"),
      api.get("/units").catch(()=>({data: []})),
    ]);
    setData(d.data); setPlans(p.data); setUnitsList(u.data || []);
  };
  useEffect(() => { if (open && studentId) { setTab("resumo"); load(); } }, [open, studentId]);
  useEffect(() => { return () => { streamRef.current?.getTracks().forEach(t => t.stop()); }; }, []);
  // Fix mobile camera black screen: só atribui srcObject depois que o <video> foi renderizado
  useEffect(() => {
    if (photoStreaming && streamRef.current && videoRef.current) {
      const v = videoRef.current;
      try { v.srcObject = streamRef.current; } catch {}
      v.muted = true;
      v.setAttribute("playsinline", "true");
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    }
  }, [photoStreaming]);
  useEffect(() => {
    if (!transferOpen) { return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/students", { params: { page: 1, page_size: 30, q: transferSearch.trim(), all_units: true } });
        setAllStudents(data.items || []);
      } catch { setAllStudents([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [transferOpen, transferSearch]);

  if (!open || !data) return null;
  const s = data.student, plan = data.plan, unit = data.unit, stats = data.stats;
  const age = calcAge(s.birthdate);
  const now = new Date();
  const memEnd = endOfValidity(s.membership_end);
  const isSuspended = !!s.suspended;
  const isActive = !!(memEnd && memEnd > now && !s.blocked && !isSuspended);

  const canChangeUnit = !!currentUser && !["student", "trainer"].includes(currentUser.role);
  const openUnitChange = () => {
    setUnitId(s.unit_id || "");
    setUnitOpen(true);
  };
  const doChangeUnit = async () => {
    if (!unitId) return toast.error("Selecione uma unidade");
    if (unitId === s.unit_id) return setUnitOpen(false);
    setUnitSaving(true);
    try {
      await api.patch(`/students/${studentId}`, { unit_id: unitId });
      toast.success("Unidade padrão atualizada");
      setUnitOpen(false); load(); onUpdate && onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setUnitSaving(false); }
  };

  const doRenew = async () => {
    if (!plan) { setTab("vendas"); return toast.info("Aluno sem plano atual — escolha o plano para renovar."); }
    // pix_qrcode e pix_mp abrem o diálogo MP (QR Code real). pix_maquineta vai como "pix" normal.
    if (renewMethod === "pix_mp" || renewMethod === "pix_qrcode") { setMpRenewOpen(true); return; }
    const effectiveMethod = renewMethod === "pix_maquineta" ? "pix" : renewMethod;
    setRenewing(true);
    try {
      await api.post(`/students/${studentId}/renew?payment_method=${effectiveMethod}`);
      toast.success(`Renovado: ${plan?.name || "plano"}`);
      load(); onUpdate && onUpdate();
    } catch (e) {
      if (e.response?.status === 400 && e.response?.data?.detail?.code === "no_current_plan") {
        setTab("vendas");
        toast.info(e.response.data.detail.message);
      } else if (e.response?.status === 423 && e.response?.data?.detail?.code === "cash_closed") {
        const info = e.response.data.detail;
        const reason = window.prompt(
          `⚠️ CAIXA FECHADO\n\nO caixa de hoje (${info.date}) foi fechado por ${info.closed_by_name || "—"}.\n\nPara registrar essa renovação, é preciso reabrir o caixa.\nInforme o MOTIVO (ficará no histórico de auditoria):`,
          ""
        );
        if (reason === null) { setRenewing(false); return; }
        const r = (reason || "").trim();
        if (!r) { setRenewing(false); return toast.error("Motivo obrigatório"); }
        try {
          const q = new URLSearchParams({ date: info.date, reason: r, ...(info.unit_id ? { unit_id: info.unit_id } : {}) }).toString();
          await api.post(`/finance/reopen-day?${q}`);
          toast.success("Caixa reaberto — refazendo renovação...");
          setRenewing(false);
          return doRenew();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Falha ao reabrir caixa");
        }
      } else {
        toast.error(e.response?.data?.detail || "Erro");
      }
    }
    finally { setRenewing(false); }
  };
  const doSell = async () => {
    if (!sellPlan) return toast.error("Selecione um plano");
    // Pix via Mercado Pago → abre dialog com QR real
    if (sellMethod === "pix_mp") { setMpSellOpen(true); return; }
    const p = plans.find(x => x.id === sellPlan);
    const finalPrice = p ? Number((p.price * (1 - (sellDiscount || 0) / 100)).toFixed(2)) : 0;
    const splitAmt = parseFloat(sellSplitAmount) || 0;
    // pix_maquineta maps to "pix" for financial reporting (mesmo comportamento da Venda Rápida)
    const apiMethod = sellMethod === "pix_maquineta" ? "pix" : sellMethod;
    if (sellSplit) {
      if (sellSplitMethod === apiMethod) return toast.error("Escolha uma segunda forma de pagamento diferente da primeira");
      if (splitAmt <= 0 || splitAmt >= finalPrice) return toast.error(`Valor da 2ª forma deve estar entre R$ 0,01 e R$ ${(finalPrice - 0.01).toFixed(2)}`);
    }
    setSelling(true);
    try {
      const payload = { plan_id: sellPlan, payment_method: apiMethod, mark_paid: true, discount_percent: sellDiscount };
      if (sellSplit) { payload.split_method = sellSplitMethod; payload.split_amount = splitAmt; }
      if (sellStartCustom) {
        if (!sellStartDate) { setSelling(false); return toast.error("Escolha a data de início do plano"); }
        payload.start_date = sellStartDate;
      }
      await api.post(`/students/${studentId}/sell-plan`, payload);
      toast.success(sellSplit ? `Venda dividida: ${METHOD_LABEL[sellMethod]} R$ ${(finalPrice - splitAmt).toFixed(2)} + ${METHOD_LABEL[sellSplitMethod]} R$ ${splitAmt.toFixed(2)}` : (sellDiscount > 0 ? `Plano vendido com ${sellDiscount}% de desconto` : "Plano vendido com sucesso"));
      setSellPlan(""); setSellDiscount(0); setSellSplit(false); setSellSplitAmount(""); setSellStartCustom(false); setSellStartDate(""); load(); onUpdate && onUpdate();
    } catch (e) {
      if (e.response?.status === 423 && e.response?.data?.detail?.code === "cash_closed") {
        const info = e.response.data.detail;
        const reason = window.prompt(
          `⚠️ CAIXA FECHADO\n\nO caixa de hoje (${info.date}) foi fechado por ${info.closed_by_name || "—"}.\n\nPara registrar essa venda, é preciso reabrir o caixa.\nInforme o MOTIVO (ficará no histórico de auditoria):`,
          ""
        );
        if (reason === null) { setSelling(false); return; }
        const r = (reason || "").trim();
        if (!r) { setSelling(false); return toast.error("Motivo obrigatório"); }
        try {
          const q = new URLSearchParams({ date: info.date, reason: r, ...(info.unit_id ? { unit_id: info.unit_id } : {}) }).toString();
          await api.post(`/finance/reopen-day?${q}`);
          toast.success("Caixa reaberto — refazendo venda...");
          setSelling(false);
          return doSell();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Falha ao reabrir caixa");
        }
      } else {
        toast.error(e.response?.data?.detail || "Erro");
      }
    }
    finally { setSelling(false); }
  };
  const doAddDays = async () => {
    if (!addDaysForm.days || addDaysForm.days <= 0) return toast.error("Informe uma quantidade de dias válida");
    setAddDaysSaving(true);
    try {
      const { data: r } = await api.post(`/students/${studentId}/add-days`, addDaysForm);
      toast.success(`${r.days_added} dia(s) adicionado(s) ao plano`);
      setAddDaysOpen(false); setAddDaysForm({ days: 7, reason: "" }); load(); onUpdate && onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setAddDaysSaving(false); }
  };
  const doRemoveDays = async () => {
    if (!rmDaysForm.days || rmDaysForm.days <= 0) return toast.error("Informe uma quantidade de dias válida");
    if (!rmDaysForm.reason.trim()) return toast.error("Informe o motivo");
    setRmDaysSaving(true);
    try {
      const { data: r } = await api.post(`/students/${studentId}/remove-days`, rmDaysForm);
      toast.success(`${r.days_removed} dia(s) retirado(s) do plano`);
      setRmDaysOpen(false); setRmDaysForm({ days: 1, reason: "" }); load(); onUpdate && onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setRmDaysSaving(false); }
  };
  const openDelete = (tx) => { setDelTx(tx); setDelReason(""); setDelOpen(true); };
  const confirmDelete = async () => {
    if (!delReason.trim()) return toast.error("Informe o motivo");
    try {
      const r = await api.delete(`/sales/plan/${delTx.session_id}?reason=${encodeURIComponent(delReason.trim())}`);
      toast.success(r.data.still_active ? "Venda excluída — aluno segue ativo com outro plano" : "Venda excluída");
      setDelOpen(false); setDelTx(null); load(); onUpdate && onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const openWhatsApp = () => {
    if (!s.phone) return toast.error("Aluno sem telefone cadastrado");
    const phone = s.phone.replace(/\D/g, "");
    const msg = waMsg.replace("{nome}", s.name.split(" ")[0]) || `Olá ${s.name.split(" ")[0]}, tudo bem?`;
    window.open(`https://wa.me/55${phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  // ===== Thermal 80mm receipt for a payment =====
  const _computeValidUntil = (p, pl) => {
    if (!pl) return null;
    const paidAt = new Date(p.created_at);
    if (pl.duration_days) {
      const d = new Date(paidAt);
      d.setDate(d.getDate() + pl.duration_days - 1);
      d.setHours(23, 59, 59, 0);
      return d;
    }
    return new Date(paidAt.getTime() + (pl.duration_months || 0) * 30 * 86400000);
  };
  const printThermalReceipt = (p) => {
    const pl = plans.find(pp => pp.id === p.plan_id);
    const planName = p.plan_name || pl?.name || "Plano";
    const duration = pl ? (pl.duration_days ? `${pl.duration_days} dia(s)` : `${pl.duration_months} mês(es)`) : "—";
    const paidAt = new Date(p.created_at);
    const validUntil = _computeValidUntil(p, pl);
    const amount = ((p.amount || 0) / 100).toFixed(2);
    const method = METHOD_LABEL[p.kind] || p.kind;
    const unitName = unit?.name || "";
    const unitRazao = unit?.razao_social || "";
    const unitCnpj = unit?.cnpj || "";
    const unitAddr = unit?.address || "";
    const unitPhone = unit?.phone || "";
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Recibo — ${s.name}</title>
    <style>
      @page { size: 80mm auto; margin: 0; }
      * { box-sizing: border-box; }
      body { font-family: 'Courier New', monospace; width: 80mm; margin: 0; padding: 6mm 4mm; color: #000; font-size: 11px; line-height: 1.35; }
      .center { text-align: center; }
      .brand { font-size: 14px; font-weight: 900; letter-spacing: 1px; }
      .sub { font-size: 10px; }
      .divider { border-top: 1px dashed #000; margin: 6px 0; }
      .row { display: flex; justify-content: space-between; gap: 6px; font-size: 11px; }
      .row .k { text-transform: uppercase; }
      .row .v { font-weight: 700; text-align: right; }
      .title { font-size: 12px; font-weight: 900; text-transform: uppercase; margin: 4px 0; }
      .total { text-align: center; margin: 8px 0; }
      .total .lbl { font-size: 10px; text-transform: uppercase; }
      .total .val { font-size: 20px; font-weight: 900; }
      .foot { text-align: center; font-size: 10px; margin-top: 8px; }
      .sign-wrap { margin-top: 18px; text-align: center; font-size: 10px; }
      .sign-line { border-top: 1px solid #000; margin: 0 6mm 2px 6mm; }
      @media print { body { padding: 4mm 3mm; } }
    </style></head><body>
      <div class="center">
        <div class="brand">AMERICAN FIT</div>
        ${unitName ? `<div class="sub">${unitName}</div>` : ""}
        ${unitRazao ? `<div class="sub" style="font-weight:700">${unitRazao}</div>` : ""}
        ${unitCnpj ? `<div class="sub">CNPJ: ${unitCnpj}</div>` : ""}
        ${unitAddr ? `<div class="sub">${unitAddr}</div>` : ""}
        ${unitPhone ? `<div class="sub">Tel: ${unitPhone}</div>` : ""}
        <div class="divider"></div>
        <div class="sub">RECIBO DE PAGAMENTO</div>
        <div class="sub">Nº ${(p.session_id || "").toString().slice(-8).toUpperCase()}</div>
        <div class="sub">${paidAt.toLocaleString("pt-BR")}</div>
      </div>
      <div class="divider"></div>
      <div class="title">DADOS DO ALUNO</div>
      <div class="row"><span class="k">Nome</span><span class="v">${s.name}</span></div>
      ${s.cpf ? `<div class="row"><span class="k">CPF</span><span class="v">${maskCPF(s.cpf)}</span></div>` : ""}
      ${s.phone ? `<div class="row"><span class="k">Tel</span><span class="v">${maskPhone(s.phone)}</span></div>` : ""}
      <div class="divider"></div>
      <div class="title">SERVIÇO</div>
      <div class="row"><span class="k">Plano</span><span class="v">${planName}</span></div>
      <div class="row"><span class="k">Duração</span><span class="v">${duration}</span></div>
      ${validUntil ? `<div class="row"><span class="k">Válido até</span><span class="v">${validUntil.toLocaleDateString("pt-BR")}</span></div>` : ""}
      <div class="row"><span class="k">Pagamento</span><span class="v">${method}</span></div>
      <div class="row"><span class="k">Status</span><span class="v">${STATUS_LABEL[p.payment_status] || p.payment_status}</span></div>
      <div class="divider"></div>
      <div class="total">
        <div class="lbl">VALOR PAGO</div>
        <div class="val">R$ ${amount}</div>
      </div>
      <div class="divider"></div>
      <div class="sign-wrap">
        <div style="height:28px"></div>
        <div class="sign-line"></div>
        <div>Assinatura do responsável</div>
        ${unitRazao ? `<div style="margin-top:2px">${unitRazao}</div>` : ""}
      </div>
      <div class="foot">Obrigado pela preferência!</div>
      <div class="foot">Guarde este comprovante.</div>
      <script>window.onload=()=>setTimeout(()=>{window.print();},250);</script>
    </body></html>`;
    const w = window.open("", "_blank", "width=380,height=700");
    if (!w) return toast.error("Popup bloqueado pelo navegador");
    w.document.write(html); w.document.close();
  };

  const sendReceiptWhatsApp = (p) => {
    if (!s.phone) return toast.error("Aluno sem telefone cadastrado");
    const phone = s.phone.replace(/\D/g, "");
    const pl = plans.find(pp => pp.id === p.plan_id);
    const planName = p.plan_name || pl?.name || "Plano";
    const paidAt = new Date(p.created_at);
    const validUntil = _computeValidUntil(p, pl);
    const amount = ((p.amount || 0) / 100).toFixed(2);
    const method = METHOD_LABEL[p.kind] || p.kind;
    const msg = `Olá ${s.name.split(" ")[0]}! 💪%0A%0ASegue o comprovante do seu pagamento na *American Fit*:%0A%0A📋 *Plano:* ${planName}%0A💰 *Valor:* R$ ${amount}%0A💳 *Pagamento:* ${method}%0A📅 *Data:* ${paidAt.toLocaleDateString("pt-BR")}${validUntil ? `%0A✅ *Válido até:* ${validUntil.toLocaleDateString("pt-BR")}` : ""}%0A%0AObrigado pela preferência! 🙌`;
    window.open(`https://wa.me/55${phone}?text=${msg}`, "_blank");
  };

  // ===== Cadastro edit =====
  const openCadastro = () => {
    setCadastroForm({
      name: s.name || "", email: s.email || "", cpf: maskCPF(s.cpf || ""), phone: maskPhone(s.phone || ""),
      birthdate: brDateFromISO(s.birthdate || ""), gender: s.gender || "",
      address: s.address || "", address_number: s.address_number || "",
      address_complement: s.address_complement || "", neighborhood: s.neighborhood || "",
      city: s.city || "", state: s.state || "", zip_code: maskCEP(s.zip_code || ""),
      emergency_contact_name: s.emergency_contact_name || "",
      emergency_contact_phone: maskPhone(s.emergency_contact_phone || ""),
      goal: s.goal || "", experience_level: s.experience_level || "iniciante",
      notes: s.notes || "",
    });
    setCadastroOpen(true);
  };
  const onCepChange = async (val) => {
    const masked = maskCEP(val);
    setCadastroForm((f) => ({ ...f, zip_code: masked }));
    if (unmask(masked).length === 8) {
      setCepLoading(true);
      const found = await lookupCEP(masked);
      setCepLoading(false);
      if (found) {
        setCadastroForm((f) => ({
          ...f,
          address: found.address || f.address,
          neighborhood: found.neighborhood || f.neighborhood,
          city: found.city || f.city,
          state: found.state || f.state,
        }));
        toast.success("Endereço preenchido via ViaCEP");
      } else {
        toast.error("CEP não encontrado");
      }
    }
  };
  const saveCadastro = async () => {
    setCadastroSaving(true);
    try {
      const payload = { ...cadastroForm };
      if (payload.birthdate) payload.birthdate = isoFromBRDate(payload.birthdate) || payload.birthdate;
      await api.patch(`/students/${studentId}`, payload);
      toast.success("Cadastro atualizado");
      setCadastroOpen(false); load(); onUpdate && onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setCadastroSaving(false); }
  };

  // ===== Mais Ações =====
  const toggleActive = async () => {
    try {
      await api.patch(`/students/${studentId}`, { active: !s.active });
      toast.success(s.active ? "Aluno desativado" : "Aluno ativado");
      setMoreOpen(false); load(); onUpdate && onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const deleteStudent = async () => {
    if (!window.confirm(`Excluir permanentemente ${s.name}? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/students/${studentId}`);
      toast.success("Aluno excluído");
      setMoreOpen(false); onClose(); onUpdate && onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const setPortalPassword = async () => {
    if (!newPwd || newPwd.length < 4) return toast.error("Senha mínima de 4 caracteres");
    try {
      await api.patch(`/students/${studentId}`, { portal_password: newPwd });
      toast.success(s.has_portal_password ? "Senha do portal alterada" : "Senha do portal definida");
      setPwdOpen(false); setNewPwd(""); setMoreOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  // ===== Transferir dias / Suspender =====
  const openTransfer = async () => {
    setMoreOpen(false);
    setTransferForm({ target_student_id: "", reason: "" });
    setTransferSearch("");
    setTransferOpen(true);
  };
  const doTransfer = async () => {
    if (!transferForm.target_student_id) return toast.error("Selecione o aluno destino");
    if (!transferForm.reason.trim()) return toast.error("Descreva a justificativa da transferência");
    setTransferSaving(true);
    try {
      const { data } = await api.post(`/students/${studentId}/transfer-days`, transferForm);
      toast.success(`${data.days_transferred} dia(s) transferidos com sucesso`);
      setTransferOpen(false); load(); onUpdate && onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro na transferência"); }
    finally { setTransferSaving(false); }
  };
  const openSuspend = () => {
    setMoreOpen(false);
    setSuspendForm({ reason: "", category: "saude" });
    setSuspendOpen(true);
  };
  const doSuspend = async () => {
    if (!suspendForm.reason.trim()) return toast.error("Descreva o motivo da suspensão");
    setSuspendSaving(true);
    try {
      const { data } = await api.post(`/students/${studentId}/suspend`, suspendForm);
      toast.success(`Aluno suspenso — ${data.suspended_days_left} dia(s) guardados`);
      setSuspendOpen(false); load(); onUpdate && onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSuspendSaving(false); }
  };
  const doUnsuspend = async () => {
    if (!window.confirm(`Retirar a suspensão de ${s.name}? Os dias guardados voltam a contar a partir de hoje.`)) return;
    try {
      const { data } = await api.post(`/students/${studentId}/unsuspend`);
      toast.success(`Suspensão retirada — ${data.days_restored} dia(s) restaurados`);
      setMoreOpen(false); load(); onUpdate && onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  // ===== Workouts =====
  const emptyExercise = () => ({ name: "", sets: 3, reps: "12", rest_seconds: 60, load: "", notes: "" });
  const openWkCopy = async () => {
    setWkCopyOpen(true);
    setWkCopyTab("students");
    setWkCopySearch("");
    setWkCopyLoading(true);
    try {
      const [wkRes, tplRes, stRes] = await Promise.all([
        api.get("/workouts"),
        api.get("/workouts/templates"),
        api.get("/students"),
      ]);
      // Não mostrar treinos do próprio aluno
      const others = (wkRes.data || []).filter(w => w.student_id && w.student_id !== studentId);
      setWkCopyList(others);
      setWkCopyTemplates(tplRes.data || []);
      const map = {};
      (stRes.data || []).forEach(s => { map[s.id] = s.name; });
      setWkCopyStudentsMap(map);
    } catch (e) { toast.error("Erro ao carregar treinos"); }
    finally { setWkCopyLoading(false); }
  };

  const applyWkFromSource = (w) => {
    // Fecha modal e abre o formulário Novo Treino já preenchido — usuário revisa e confirma
    setWkCopyOpen(false);
    setWkEditingId(null);
    const sourceLabel = w.is_template
      ? "Biblioteca"
      : (wkCopyStudentsMap[w.student_id] || "outro aluno");
    setWkManualForm({
      title: `${w.title} (baseado em ${sourceLabel})`,
      goal: w.goal || "",
      days: (w.days || []).map(d => ({
        label: d.label,
        focus: d.focus || "",
        exercises: (d.exercises || []).map(e => ({ ...emptyExercise(), ...e })),
      })),
    });
    setWkManualOpen(true);
    toast.info("Ficha pré-carregada — revise e salve para o aluno");
  };

  const saveAsTemplate = async (w) => {
    setWkTplSaving(x => ({ ...x, [w.id]: true }));
    try {
      await api.post(`/workouts/${w.id}/save-as-template`);
      toast.success("Treino salvo na Biblioteca de Treinos Salvos");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar template"); }
    finally { setWkTplSaving(x => ({ ...x, [w.id]: false })); }
  };

  const openWkManual = () => {
    setWkEditingId(null);
    setWkManualForm({
      title: `Ficha ${s.name.split(" ")[0]}`,
      goal: s.goal || "",
      days: [{ label: "A", focus: "", exercises: [emptyExercise()] }],
    });
    setWkManualOpen(true);
  };
  const openWkEdit = (w) => {
    setWkEditingId(w.id);
    setWkManualForm({
      title: w.title || "",
      goal: w.goal || "",
      days: w.days.map(d => ({
        label: d.label,
        focus: d.focus || "",
        exercises: (d.exercises || []).map(e => ({ ...emptyExercise(), ...e })),
      })),
    });
    setWkManualOpen(true);
  };
  const openWkDuplicate = (w) => {
    setWkEditingId(null);
    setWkManualForm({
      title: `${w.title} (cópia)`,
      goal: w.goal || "",
      days: w.days.map(d => ({
        label: d.label,
        focus: d.focus || "",
        exercises: (d.exercises || []).map(e => ({ ...emptyExercise(), ...e })),
      })),
    });
    setWkManualOpen(true);
    toast.info("Ficha duplicada — ajuste e salve");
  };
  const addDay = () => {
    setWkManualForm(f => ({ ...f, days: [...f.days, { label: String.fromCharCode(65 + f.days.length), focus: "", exercises: [emptyExercise()] }] }));
  };
  const removeDay = (di) => setWkManualForm(f => ({ ...f, days: f.days.filter((_, i) => i !== di) }));
  const addExercise = (di) => {
    setWkManualForm(f => {
      const days = [...f.days]; days[di].exercises = [...days[di].exercises, emptyExercise()]; return { ...f, days };
    });
  };
  const removeExercise = (di, ei) => {
    setWkManualForm(f => {
      const days = [...f.days]; days[di].exercises = days[di].exercises.filter((_, i) => i !== ei); return { ...f, days };
    });
  };
  const updateExercise = (di, ei, field, value) => {
    setWkManualForm(f => {
      const days = [...f.days]; days[di].exercises[ei] = { ...days[di].exercises[ei], [field]: value }; return { ...f, days };
    });
  };
  const updateDay = (di, field, value) => {
    setWkManualForm(f => { const days = [...f.days]; days[di] = { ...days[di], [field]: value }; return { ...f, days }; });
  };
  const saveWkManual = async () => {
    if (!wkManualForm.title.trim()) return toast.error("Informe um título");
    if (wkManualForm.days.some(d => d.exercises.length === 0 || d.exercises.some(e => !e.name.trim()))) return toast.error("Preencha o nome de todos os exercícios");
    setWkSaving(true);
    try {
      if (wkEditingId) {
        await api.put(`/workouts/${wkEditingId}`, { ...wkManualForm, student_id: studentId });
        toast.success("Treino atualizado");
      } else {
        await api.post("/workouts", { ...wkManualForm, student_id: studentId });
        toast.success("Treino salvo");
      }
      setWkManualOpen(false); setWkEditingId(null); load(); onUpdate && onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setWkSaving(false); }
  };
  const generateWkAI = async () => {
    if (!wkAIForm.goal.trim()) return toast.error("Informe o objetivo do treino");
    setWkGenerating(true);
    try {
      await api.post("/workouts/ai-generate", { student_id: studentId, experience_level: s.experience_level || "iniciante", ...wkAIForm });
      toast.success("Treino gerado pela IA");
      setWkAIOpen(false); load(); onUpdate && onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao gerar"); }
    finally { setWkGenerating(false); }
  };
  const deleteWorkout = async (wid) => {
    if (!window.confirm("Excluir esta ficha de treino?")) return;
    try {
      await api.delete(`/workouts/${wid}`);
      toast.success("Treino removido"); load(); onUpdate && onUpdate();
    } catch (e) { toast.error("Erro"); }
  };
  const toggleWk = (wid) => setWkExpanded(m => ({ ...m, [wid]: !m[wid] }));
  const openWkDupTo = async (w) => {
    setWkDupWorkout(w);
    setWkDupTargets([]);
    setWkDupSearch("");
    setWkDupOpen(true);
    if (allStudents.length === 0) {
      try { const { data } = await api.get("/students"); setAllStudents(data); } catch {}
    }
  };
  const toggleDupTarget = (sid) => {
    setWkDupTargets(prev => prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]);
  };
  const doWkDupTo = async () => {
    if (wkDupTargets.length === 0) return toast.error("Selecione ao menos 1 aluno de destino");
    setWkDupSaving(true);
    try {
      const { data } = await api.post(`/workouts/${wkDupWorkout.id}/duplicate`, { target_student_ids: wkDupTargets });
      toast.success(`Treino duplicado para ${data.count} aluno(s)`);
      setWkDupOpen(false); setWkDupWorkout(null); setWkDupTargets([]);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao duplicar"); }
    finally { setWkDupSaving(false); }
  };

  // ===== Video attachments =====
  const openVideoPicker = async (di, ei) => {
    setVideoPickerTarget({ di, ei });
    setVideoTab("library");
    setYtUrl("");
    setLibrarySearch("");
    setVideoPickerOpen(true);
    if (library.length === 0) {
      try { const { data } = await api.get("/exercise-library"); setLibrary(data); } catch {}
    }
  };
  const setExerciseVideo = (url) => {
    const { di, ei } = videoPickerTarget;
    updateExercise(di, ei, "video_url", url);
    setVideoPickerOpen(false);
    toast.success("Vídeo anexado ao exercício");
  };
  const ytToEmbed = (url) => {
    if (!url) return null;
    if (url.includes("/embed/")) return url;
    const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|v\/))([\w-]{11})/);
    if (m) return `https://www.youtube.com/embed/${m[1]}`;
    return null;
  };
  const applyYouTube = () => {
    const embed = ytToEmbed(ytUrl.trim());
    if (!embed) return toast.error("Cole uma URL válida do YouTube (watch?v=... ou youtu.be/...)");
    setExerciseVideo(embed);
  };
  const uploadVideo = async (file) => {
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) return toast.error("Arquivo muito grande (limite 30MB)");
    const reader = new FileReader();
    reader.onload = () => setExerciseVideo(reader.result);
    reader.readAsDataURL(file);
    toast.info("Convertendo vídeo...");
  };
  const isYouTube = (u) => u && (u.includes("youtube.com") || u.includes("youtu.be"));
  const isUpload = (u) => u && u.startsWith("data:video");

  const printWorkout = (w) => {
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${w.title} — ${s.name}</title>
    <style>
      * { box-sizing: border-box; }
      body { font-family: -apple-system, 'Segoe UI', system-ui, sans-serif; color: #0F172A; padding: 32px; max-width: 800px; margin: 0 auto; }
      .header { border-bottom: 4px solid #E11D48; padding-bottom: 16px; margin-bottom: 24px; display: flex; align-items: center; justify-content: space-between; }
      .brand { font-size: 12px; letter-spacing: 2px; text-transform: uppercase; color: #E11D48; font-weight: 900; }
      h1 { margin: 4px 0 0; font-size: 28px; text-transform: uppercase; letter-spacing: -0.5px; }
      .meta { color: #64748B; font-size: 13px; margin-top: 4px; }
      .info-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; background: #F8FAFC; padding: 16px; border-radius: 8px; margin-bottom: 24px; }
      .info-row div { font-size: 12px; }
      .info-row strong { display: block; color: #64748B; font-size: 10px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 4px; }
      .day { margin-bottom: 20px; border: 2px solid #E2E8F0; border-radius: 12px; overflow: hidden; page-break-inside: avoid; }
      .day-header { background: #0F172A; color: #fff; padding: 12px 16px; display: flex; align-items: center; gap: 12px; }
      .day-label { background: #E11D48; color: #fff; width: 40px; height: 40px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-weight: 900; font-size: 20px; }
      .day-focus { font-weight: 700; font-size: 16px; text-transform: uppercase; letter-spacing: 0.5px; }
      table { width: 100%; border-collapse: collapse; }
      th { background: #F8FAFC; text-align: left; padding: 10px 12px; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #64748B; border-bottom: 1px solid #E2E8F0; }
      td { padding: 10px 12px; border-bottom: 1px solid #F1F5F9; font-size: 13px; }
      td.name { font-weight: 700; color: #0F172A; }
      td.center { text-align: center; color: #475569; }
      .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #E2E8F0; color: #94A3B8; font-size: 11px; text-align: center; }
      @media print { body { padding: 16px; } .no-print { display: none; } }
    </style></head><body>
      <div class="header">
        <div>
          <div class="brand">American Fit — Ficha de Treino</div>
          <h1>${w.title}</h1>
          <div class="meta">${s.name} · Gerado em ${new Date().toLocaleDateString("pt-BR")}${w.generated_by_ai ? " · Por IA (GPT-5.4)" : ""}</div>
        </div>
      </div>
      <div class="info-row">
        <div><strong>Objetivo</strong>${w.goal || "—"}</div>
        <div><strong>Nível</strong>${s.experience_level || "—"}</div>
        <div><strong>Fichas</strong>${w.days.length} · ${w.days.map(d=>d.label).join(" / ")}</div>
      </div>
      ${w.days.map(d => `
        <div class="day">
          <div class="day-header">
            <div class="day-label">${d.label}</div>
            <div class="day-focus">${d.focus || "Ficha " + d.label}</div>
          </div>
          <table>
            <thead><tr><th>Exercício</th><th style="text-align:center">Séries</th><th style="text-align:center">Reps</th><th style="text-align:center">Descanso</th><th style="text-align:center">Carga</th><th>Notas</th></tr></thead>
            <tbody>${d.exercises.map(e => `
              <tr>
                <td class="name">${e.name}</td>
                <td class="center">${e.sets}</td>
                <td class="center">${e.reps}</td>
                <td class="center">${e.rest_seconds}s</td>
                <td class="center">${e.load || "—"}</td>
                <td>${e.notes || ""}</td>
              </tr>`).join("")}</tbody>
          </table>
        </div>
      `).join("")}
      <div class="footer">American Fit · fitpass.com.br · Ficha personalizada para ${s.name}</div>
      <script>window.onload=()=>setTimeout(()=>window.print(),400);</script>
    </body></html>`;
    const w2 = window.open("", "_blank", "width=900,height=700");
    if (!w2) return toast.error("Popup bloqueado pelo navegador");
    w2.document.write(html); w2.document.close();
  };

  // ===== Quick Photo Capture =====
  const openPhoto = async () => {
    setPhotoOpen(true);
    if (!modelsReady) {
      try { await loadFaceModels(); setModelsReady(true); }
      catch { toast.error("Falha carregando modelos faciais"); }
    }
  };
  const startPhotoCam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "user" },
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
        audio: false,
      });
      streamRef.current = stream;
      setPhotoStreaming(true); // atribui srcObject no useEffect abaixo quando <video> estiver no DOM
    } catch (e) {
      toast.error("Sem acesso à câmera" + (e?.message ? `: ${e.message}` : ""));
    }
  };
  const stopPhotoCam = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    setPhotoStreaming(false);
  };
  const capturePhoto = async () => {
    const video = videoRef.current, canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
    setPhotoSaving(true);
    try {
      const desc = await detectEnrollFace(video);
      if (desc.problem) { toast.error(desc.problem); return; }
      const payload = { face_photo: dataUrl };
      if (desc?.descriptor) payload.face_embedding = desc.descriptor;
      await api.patch(`/students/${studentId}`, payload);
      toast.success(desc ? "Foto atualizada + rosto 128D reconhecido" : "Foto atualizada");
      stopPhotoCam(); setPhotoOpen(false);
      load(); onUpdate && onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    finally { setPhotoSaving(false); }
  };

  const removePhoto = async () => {
    if (!window.confirm(`Apagar a foto facial de ${s.name}? O rosto também será removido do leitor facial e o aluno não entra pela facial até cadastrar outra.`)) return;
    setPhotoSaving(true);
    try {
      await api.patch(`/students/${studentId}`, { face_photo: "", face_embedding: "" });
      toast.success("Foto facial apagada");
      load(); onUpdate && onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao apagar a foto"); }
    finally { setPhotoSaving(false); }
  };

  // Build contracts list from payments (paid plans)
  const contracts = (data.payments || [])
    .filter(p => p.payment_status === "paid" && p.plan_id)
    .map(p => {
      const pl = plans.find(pp => pp.id === p.plan_id);
      const duration = pl ? (pl.duration_days ? `${pl.duration_days} Dia${pl.duration_days > 1 ? "s" : ""}` : `${pl.duration_months} Mês${pl.duration_months > 1 ? "es" : ""}`) : "—";
      const paidAt = new Date(p.created_at);
      // Regra: planos DIÁRIOS encerram às 23:59:59 do (dia_da_venda + duration_days - 1)
      // Mensais mantêm cálculo antigo (dias × 30).
      let validUntil;
      if (pl?.duration_days) {
        validUntil = new Date(paidAt);
        validUntil.setDate(validUntil.getDate() + pl.duration_days - 1);
        validUntil.setHours(23, 59, 59, 0);
      } else {
        const days = (pl?.duration_months || 0) * 30;
        validUntil = new Date(paidAt.getTime() + days * 86400000);
      }
      const active = validUntil > now && p.session_id && plan?.id === p.plan_id && memEnd && Math.abs(validUntil - memEnd) < 86400000;
      return { ...p, plan_name: (pl?.name || "PLANO").toUpperCase(), duration, validUntil, active };
    });
  // Only newest is "Ativo" (or "Suspenso" if student is suspended); rest "Encerrado"
  const contractsMarked = contracts.map((c, i) => ({ ...c, active: i === 0 && (isActive || isSuspended) }));

  return (
    <Dialog open={open} onOpenChange={(v)=>!v && onClose()}>
      <DialogContent className="max-w-none w-screen lg:w-[calc(100vw-16rem)] h-screen h-[100dvh] rounded-none p-0 overflow-hidden gap-0 bg-slate-50 top-0 left-0 lg:left-64 translate-x-0 translate-y-0 border-0 flex flex-col [&>button.absolute]:hidden data-[state=open]:![animation:af-fade-in_150ms_ease-out] data-[state=closed]:![animation:af-fade-out_120ms_ease-in]" data-testid="student-detail-dialog">
        {/* Header */}
        <div className="relative bg-white border-b border-slate-200 px-6 pb-0 shrink-0 pt-[calc(1.5rem+env(safe-area-inset-top,0px))]">
          <button onClick={onClose} aria-label="Fechar" className="absolute right-3 top-[calc(0.75rem+env(safe-area-inset-top,0px))] z-20 w-11 h-11 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 shadow-sm" data-testid="detail-close-button"><X className="w-5 h-5"/></button>
          <div className="flex items-start gap-5 flex-wrap">
            <div className="relative shrink-0">
              <div className="w-24 h-24 rounded-full border-4 border-rose-500 overflow-hidden bg-slate-100 flex items-center justify-center">
                {s.face_photo ? (
                  <img src={s.face_photo} alt={s.name} className="w-full h-full object-cover"/>
                ) : (
                  <span className="font-heading text-3xl font-bold text-slate-400">{s.name[0]}</span>
                )}
              </div>
              <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-rose-500 text-white flex items-center justify-center shadow-lg cursor-pointer hover:bg-rose-600 transition-colors" title="Alterar foto" onClick={openPhoto} data-testid="detail-photo-button">
                <Camera className="w-4 h-4"/>
              </div>
              {s.face_photo && (
                <button type="button" onClick={removePhoto} disabled={photoSaving} title="Apagar foto facial" aria-label="Apagar foto facial"
                  className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-white border-2 border-rose-500 text-rose-600 flex items-center justify-center shadow hover:bg-rose-50 transition-colors disabled:opacity-50"
                  data-testid="detail-photo-delete-button">
                  <X className="w-4 h-4"/>
                </button>
              )}
            </div>

            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900 uppercase tracking-tight" data-testid="detail-student-name">{s.name}</h1>
                {isSuspended ? (
                  <span className="flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-amber-100 text-amber-800 font-bold"><PauseCircle className="w-3.5 h-3.5"/>Suspenso</span>
                ) : isActive ? (
                  <span className="flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 font-bold"><CheckCircle2 className="w-3.5 h-3.5"/>Ativo</span>
                ) : (
                  <span className="flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-slate-200 dark:bg-slate-800 text-rose-700 dark:text-rose-400 font-bold border-2 border-rose-500"><XCircle className="w-3.5 h-3.5"/>Inativo</span>
                )}
                {s.blocked && !isSuspended && (
                  <span className="text-xs px-3 py-1 rounded-full bg-rose-100 text-rose-700 font-bold">Bloqueado</span>
                )}
                {s.suspended && (
                  <span className="flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-amber-100 text-amber-800 font-bold" data-testid="detail-suspended-badge">
                    <PauseCircle className="w-3.5 h-3.5"/>
                    Suspenso{s.suspended_category === "saude" ? " (Saúde)" : ""} · {s.suspended_days_left || 0} dia(s) guardados
                  </span>
                )}
              </div>
              <div className="text-sm text-slate-500 mt-1">
                {age !== null ? `${age} anos` : "—"}{s.gender ? `, ${s.gender}` : ""}
              </div>
              {isAdmin && s.id && (
                <div className="mt-1 flex items-center gap-2 text-xs text-slate-500" data-testid="detail-student-id">
                  <span>ID:</span>
                  <code className="font-mono text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded select-all" data-testid="detail-student-id-value">{s.id}</code>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-500 hover:text-sky-600" data-testid="detail-copy-id-button"
                    onClick={async () => {
                      try { await navigator.clipboard.writeText(s.id); toast.success("ID copiado"); return; } catch { /* fallback abaixo */ }
                      const ta = document.createElement("textarea");
                      ta.value = s.id; document.body.appendChild(ta); ta.select();
                      const ok = document.execCommand("copy"); ta.remove();
                      ok ? toast.success("ID copiado") : toast.error("Não foi possível copiar; selecione o ID e copie manualmente");
                    }}>
                    <Copy className="w-3.5 h-3.5 mr-1"/>Copiar ID
                  </Button>
                </div>
              )}
              {unit && (
                <div className="mt-2 inline-flex items-center gap-2">
                  <div className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg bg-rose-50 text-rose-700 font-semibold" data-testid="detail-unit-badge">
                    <Building2 className="w-3.5 h-3.5"/>{unit.name}
                  </div>
                  {canChangeUnit && (
                    <Button size="sm" variant="ghost" onClick={openUnitChange} title="Trocar unidade padrão" data-testid="detail-change-unit-button" className="h-8 w-8 p-0 text-slate-500 hover:text-rose-600 hover:bg-rose-50">
                      <Pencil className="w-3.5 h-3.5"/>
                    </Button>
                  )}
                </div>
              )}
              {!unit && canChangeUnit && (
                <Button size="sm" variant="outline" onClick={openUnitChange} className="mt-2 border-rose-300 text-rose-600 hover:bg-rose-50 font-bold uppercase text-xs" data-testid="detail-set-unit-button">
                  <Building2 className="w-3.5 h-3.5 mr-1"/>Definir unidade
                </Button>
              )}

              <div className="flex flex-wrap items-center gap-2 mt-4">
                <Button size="sm" onClick={openCadastro} className="bg-sky-500 hover:bg-sky-600 text-white font-bold uppercase" data-testid="detail-cadastro-button"><ClipboardEdit className="w-4 h-4 mr-1.5"/>Cadastro</Button>
                <Button size="sm" onClick={()=>setTab("comunicacao")} className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold uppercase" data-testid="detail-whatsapp-button"><MessageCircle className="w-4 h-4 mr-1.5"/>WhatsApp</Button>
                <div className="relative">
                  <Button size="sm" variant="outline" onClick={()=>setMoreOpen(v=>!v)} className="border-slate-300 text-slate-700 font-bold uppercase" data-testid="detail-more-button"><MoreVertical className="w-4 h-4 mr-1.5"/>Mais ações</Button>
                  {moreOpen && (
                    <>
                      {/* Backdrop (full-screen click capture) */}
                      <div className="fixed inset-0 z-40 bg-black/40 sm:bg-transparent" onClick={()=>setMoreOpen(false)}/>
                      {/* Mobile: bottom sheet; Desktop: popup absoluto */}
                      <div
                        className="fixed sm:absolute inset-x-0 bottom-0 sm:inset-auto sm:right-0 sm:top-full sm:bottom-auto sm:mt-1 z-50 w-full sm:w-64 rounded-t-2xl sm:rounded-lg border-t sm:border border-slate-200 bg-white shadow-2xl max-h-[80vh] overflow-y-auto"
                        data-testid="detail-more-menu"
                      >
                        {/* Handle visual + título só no mobile */}
                        <div className="sm:hidden">
                          <div className="w-10 h-1 bg-slate-300 rounded-full mx-auto mt-2 mb-1"/>
                          <div className="px-4 py-2 flex items-center justify-between border-b border-slate-100">
                            <span className="font-heading font-bold text-slate-900 uppercase text-sm tracking-wider">Mais ações</span>
                            <button onClick={()=>setMoreOpen(false)} className="text-slate-500 p-1 -mr-1"><XCircle className="w-5 h-5"/></button>
                          </div>
                        </div>
                        {s.active && (
                          <button onClick={toggleActive} className="w-full flex items-center gap-3 px-4 py-3 sm:py-2.5 text-sm text-slate-700 hover:bg-slate-50 text-left" data-testid="detail-toggle-active">
                            <ShieldOff className="w-5 h-5 text-amber-600 shrink-0"/>
                            <span>Desativar aluno</span>
                          </button>
                        )}
                        <button onClick={()=>{setMoreOpen(false); setPwdOpen(true);}} className="w-full flex items-center gap-3 px-4 py-3 sm:py-2.5 text-sm text-slate-700 hover:bg-slate-50 text-left" data-testid="detail-set-portal-password">
                          <KeyRound className="w-5 h-5 text-sky-600 shrink-0"/><span>{s.has_portal_password ? "Alterar senha do portal" : "Definir senha do portal"}</span>
                        </button>
                        <button onClick={()=>{setMoreOpen(false); setQrOpen(true);}} className="w-full flex items-center gap-3 px-4 py-3 sm:py-2.5 text-sm text-slate-700 hover:bg-slate-50 text-left" data-testid="detail-portal-qr-menu">
                          <QrCode className="w-5 h-5 text-emerald-600 shrink-0"/><span>QR Code do Portal</span>
                        </button>
                        {isAdmin && (
                          <button onClick={()=>{setMoreOpen(false); setAddDaysOpen(true);}} className="w-full flex items-center gap-3 px-4 py-3 sm:py-2.5 text-sm text-slate-700 hover:bg-slate-50 text-left" data-testid="detail-add-days-menu">
                            <Gift className="w-5 h-5 text-emerald-600 shrink-0"/><span>Adicionar dias grátis (Admin)</span>
                          </button>
                        )}
                        {isAdmin && (
                          <button onClick={()=>{setMoreOpen(false); setRmDaysOpen(true);}} className="w-full flex items-center gap-3 px-4 py-3 sm:py-2.5 text-sm text-slate-700 hover:bg-slate-50 text-left" data-testid="detail-remove-days-menu">
                            <CalendarMinus className="w-5 h-5 text-rose-600 shrink-0"/><span>Retirar dias (Admin)</span>
                          </button>
                        )}
                        {isAdmin && (
                          <button onClick={openTransfer} className="w-full flex items-center gap-3 px-4 py-3 sm:py-2.5 text-sm text-slate-700 hover:bg-slate-50 text-left" data-testid="detail-transfer-days-menu">
                            <ArrowRightLeft className="w-5 h-5 text-sky-600 shrink-0"/><span>Transferir dias para outro aluno</span>
                          </button>
                        )}
                        {s.suspended ? (
                          <button onClick={doUnsuspend} className="w-full flex items-center gap-3 px-4 py-3 sm:py-2.5 text-sm text-emerald-700 hover:bg-emerald-50 text-left" data-testid="detail-unsuspend-menu">
                            <PlayCircle className="w-5 h-5 text-emerald-600 shrink-0"/><span>Retirar suspensão ({s.suspended_days_left || 0} dias)</span>
                          </button>
                        ) : (
                          <button onClick={openSuspend} className="w-full flex items-center gap-3 px-4 py-3 sm:py-2.5 text-sm text-slate-700 hover:bg-slate-50 text-left" data-testid="detail-suspend-menu">
                            <PauseCircle className="w-5 h-5 text-amber-600 shrink-0"/><span>Suspender aluno (saúde/outro)</span>
                          </button>
                        )}
                        <div className="border-t border-slate-100"/>
                        <button onClick={deleteStudent} className="w-full flex items-center gap-3 px-4 py-3 sm:py-2.5 text-sm text-rose-600 hover:bg-rose-50 text-left" data-testid="detail-delete-student">
                          <Trash2 className="w-5 h-5 shrink-0"/><span>Excluir aluno</span>
                        </button>
                        {/* padding bottom extra para safe-area iPhone */}
                        <div className="h-4 sm:h-0" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}/>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-6 -mx-6 px-6 border-b border-slate-200 overflow-x-auto">
            <div className="flex items-center gap-1 min-w-max">
              {tabs.map(t => {
                const Icon = t.icon; const active = tab === t.key;
                return (
                  <button key={t.key} onClick={()=>setTab(t.key)} data-testid={`detail-tab-${t.key}`}
                    className={`px-4 py-3 text-xs md:text-sm font-bold uppercase tracking-wider transition-all border-b-2 flex items-center gap-1.5 whitespace-nowrap ${active ? "text-rose-600 border-rose-600" : "text-slate-500 border-transparent hover:text-slate-900"}`}>
                    <Icon className="w-4 h-4"/>{t.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-6 space-y-4 max-w-7xl mx-auto w-full">
          {tab === "resumo" && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Presenças totais" value={stats.granted_count} icon={Activity} color="#10B981"/>
                <StatCard label="Total pago" value={`R$ ${stats.total_paid.toFixed(2)}`} icon={DollarSign} color="#E11D48"/>
                <StatCard label="Plano atual" value={plan?.name || "—"} icon={ClipboardList} color="#3B82F6"/>
                <StatCard label="Vence em" value={fmtDate(s.membership_end)} icon={FileText} color="#F59E0B"/>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading text-sm font-bold text-slate-500 uppercase tracking-wider">Dados do cliente</h3>
                  <Button size="sm" variant="ghost" onClick={openCadastro} className="text-sky-600 hover:bg-sky-50" data-testid="detail-edit-inline"><ClipboardEdit className="w-4 h-4 mr-1"/>Editar</Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-y-3 gap-x-6 text-sm">
                  <Info label="CPF" value={s.cpf}/>
                  <Info label="Telefone" value={s.phone}/>
                  <Info label="Email" value={s.email}/>
                  <Info label="Nascimento" value={s.birthdate}/>
                  <Info label="Gênero" value={s.gender}/>
                  <Info label="Nível" value={s.experience_level}/>
                  <Info label="Objetivo" value={s.goal}/>
                  <Info label="Início" value={fmtDate(s.membership_start)}/>
                  <Info label="Fim mensalidade" value={fmtDate(s.membership_end)}/>
                  <Info label="Face cadastrada" value={s.face_embedding ? "Sim (128D)" : "Não"}/>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="font-heading text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2"><MapPin className="w-4 h-4"/>Endereço</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-y-3 gap-x-6 text-sm">
                  <Info label="Logradouro" value={s.address}/>
                  <Info label="Número" value={s.address_number}/>
                  <Info label="Complemento" value={s.address_complement}/>
                  <Info label="Bairro" value={s.neighborhood}/>
                  <Info label="Cidade" value={s.city}/>
                  <Info label="UF" value={s.state}/>
                  <Info label="CEP" value={s.zip_code}/>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="font-heading text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2"><Phone className="w-4 h-4"/>Contato de emergência</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6 text-sm">
                  <Info label="Nome" value={s.emergency_contact_name}/>
                  <Info label="Telefone" value={s.emergency_contact_phone}/>
                </div>
                {s.notes && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Observações</div>
                    <div className="text-sm text-slate-700 whitespace-pre-wrap">{s.notes}</div>
                  </div>
                )}
              </div>
            </>
          )}

          {tab === "comunicacao" && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-heading text-lg font-bold text-slate-900 mb-1">Enviar mensagem via WhatsApp</h3>
              <p className="text-sm text-slate-500 mb-4">Personalize com {"{nome}"} para inserir o primeiro nome automaticamente.</p>
              <Textarea rows={5} value={waMsg} onChange={(e)=>setWaMsg(e.target.value)} placeholder={`Olá {nome}, tudo bem? Passando para lembrar da sua rotina de treinos essa semana!`} className="mb-3" data-testid="detail-wa-message"/>
              <div className="flex gap-2">
                <Button onClick={openWhatsApp} className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold" data-testid="detail-wa-send"><MessageCircle className="w-4 h-4 mr-2"/>Abrir WhatsApp</Button>
                <Button variant="outline" onClick={()=>setWaMsg("Olá {nome}, sua mensalidade vence em breve. Que tal renovar agora e ganhar um bônus?")}>Modelo: Renovação</Button>
                <Button variant="outline" onClick={()=>setWaMsg("Sentimos sua falta, {nome}! Vem treinar hoje com a gente 💪")}>Modelo: Retorno</Button>
              </div>
            </div>
          )}

          {tab === "vendas" && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">Vender novo plano</h3>
              <div className="grid md:grid-cols-3 gap-3">
                <Select value={sellPlan} onValueChange={setSellPlan}>
                  <SelectTrigger data-testid="detail-sell-plan"><SelectValue placeholder="Escolher plano"/></SelectTrigger>
                  <SelectContent>{plans.map(p => <SelectItem key={p.id} value={p.id}>{p.name} — R$ {p.price.toFixed(2)}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={sellMethod} onValueChange={setSellMethod}>
                  <SelectTrigger data-testid="detail-sell-method"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Dinheiro</SelectItem>
                    <SelectItem value="card">Cartão (máquina)</SelectItem>
                    <SelectItem value="pix_maquineta" data-testid="detail-method-pix-maquineta">Pix na Maquineta</SelectItem>
                    <SelectItem value="pix_mp" data-testid="detail-method-pix-mp">💠 Pix Mercado Pago (QR real)</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={doSell} disabled={selling} className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold" data-testid="detail-sell-confirm"><CheckCircle2 className="w-4 h-4 mr-2"/>{selling ? "Vendendo..." : "Confirmar venda"}</Button>
              </div>
              {sellMethod === "pix_maquineta" && (
                <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">💳 Cobre o Pix na maquineta do cartão como faz normalmente. Nenhum QR será exibido na tela.</p>
              )}

              {sellPlan && <SellStartDate plan={plans.find(x => x.id === sellPlan)} custom={sellStartCustom} setCustom={setSellStartCustom} date={sellStartDate} setDate={setSellStartDate} disabled={sellMethod === "pix_mp"}/>}

              {/* Pagamento dividido */}
              {sellPlan && (() => {
                const p = plans.find(x => x.id === sellPlan);
                const finalPrice = p ? Number((p.price * (1 - (sellDiscount || 0) / 100)).toFixed(2)) : 0;
                const splitAmt = parseFloat(sellSplitAmount) || 0;
                const remaining = Math.max(0, finalPrice - splitAmt);
                return (
                  <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="detail-split-panel">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={sellSplit} onChange={(e)=>setSellSplit(e.target.checked)} className="w-4 h-4 accent-rose-600" data-testid="detail-split-toggle"/>
                      <span className="font-heading text-sm font-bold text-slate-900 uppercase tracking-wider">Dividir pagamento</span>
                      <span className="text-xs text-slate-500">(parte em uma forma, parte em outra)</span>
                    </label>
                    {sellSplit && (
                      <div className="grid md:grid-cols-3 gap-3 mt-3" data-testid="detail-split-fields">
                        <div>
                          <Label className="text-xs">2ª forma</Label>
                          <Select value={sellSplitMethod} onValueChange={setSellSplitMethod}>
                            <SelectTrigger data-testid="detail-split-method"><SelectValue/></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="cash">Dinheiro</SelectItem>
                              <SelectItem value="card">Cartão</SelectItem>
                              <SelectItem value="pix">Pix</SelectItem>
                              <SelectItem value="manual">Manual</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Valor na 2ª forma (R$)</Label>
                          <Input type="number" step="0.01" min="0.01" max={finalPrice - 0.01} value={sellSplitAmount} onChange={(e)=>setSellSplitAmount(e.target.value)} placeholder="0,00" data-testid="detail-split-amount"/>
                        </div>
                        <div className="bg-white rounded-lg border border-slate-200 p-2 text-xs">
                          <div className="flex justify-between"><span className="text-slate-500">Total plano</span><span className="font-mono font-bold text-slate-900">R$ {finalPrice.toFixed(2)}</span></div>
                          <div className="flex justify-between mt-1"><span className="text-slate-500">{METHOD_LABEL[sellMethod]}</span><span className="font-mono font-bold text-emerald-600" data-testid="detail-split-primary-amount">R$ {remaining.toFixed(2)}</span></div>
                          <div className="flex justify-between"><span className="text-slate-500">{METHOD_LABEL[sellSplitMethod]}</span><span className="font-mono font-bold text-rose-600">R$ {splitAmt.toFixed(2)}</span></div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
              {isAdmin && (
                <div className="mt-4 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 p-4" data-testid="detail-discount-panel">
                  <div className="flex items-center gap-2 mb-2">
                    <Percent className="w-4 h-4 text-amber-600"/>
                    <span className="font-heading text-sm font-bold text-amber-900 uppercase tracking-wider">Desconto (Admin)</span>
                    <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 font-bold">até 100%</span>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <input type="range" min="0" max="100" step="5" value={sellDiscount} onChange={(e)=>setSellDiscount(parseFloat(e.target.value))} className="flex-1 min-w-[200px] accent-rose-600" data-testid="detail-discount-slider"/>
                    <div className="flex items-center gap-2">
                      <Input type="number" min="0" max="100" value={sellDiscount} onChange={(e)=>setSellDiscount(Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))} className="w-20" data-testid="detail-discount-input"/>
                      <span className="font-bold text-amber-900">%</span>
                    </div>
                    {sellPlan && (() => {
                      const p = plans.find(x => x.id === sellPlan);
                      if (!p) return null;
                      const final = p.price * (1 - sellDiscount / 100);
                      return (
                        <div className="text-sm">
                          <span className="text-slate-500 line-through">R$ {p.price.toFixed(2)}</span>
                          <span className="ml-2 font-heading font-bold text-emerald-600 text-lg" data-testid="detail-discount-final">R$ {final.toFixed(2)}</span>
                        </div>
                      );
                    })()}
                  </div>
                  <p className="text-xs text-amber-800 mt-2">Apenas administradores podem aplicar desconto. Valor final é registrado no caixa e o plano tem duração normal.</p>
                </div>
              )}
              <p className="text-xs text-slate-500 mt-3">Mensalidade é estendida a partir do vencimento atual (empilha se ainda ativa).</p>
            </div>
          )}

          {tab === "contratos" && (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-3 sm:px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-heading text-lg font-bold text-slate-900">Contratos</h3>
                {((plan && isActive) || !plan) && (
                  <div className="flex items-center gap-2">
                    <Select value={renewMethod} onValueChange={setRenewMethod}>
                      <SelectTrigger className="h-9 w-44" data-testid="detail-renew-method"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Dinheiro</SelectItem>
                        <SelectItem value="card">Cartão (máquina)</SelectItem>
                        <SelectItem value="pix_maquineta" data-testid="detail-renew-pix-maquineta">Pix na Maquineta</SelectItem>
                        <SelectItem value="pix_mp" data-testid="detail-renew-pix-mp">💠 Pix Mercado Pago (QR real)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button size="sm" onClick={doRenew} disabled={renewing} className="bg-rose-600 hover:bg-rose-700 text-white font-bold uppercase" data-testid="detail-renew-btn"><RefreshCw className="w-4 h-4 mr-1.5"/>{renewing ? "..." : "Renovar"}</Button>
                  </div>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                      <th className="px-3 sm:px-5 py-3 whitespace-nowrap">Descrição</th>
                      <th className="px-3 sm:px-5 py-3 whitespace-nowrap">Duração</th>
                      <th className="px-3 sm:px-5 py-3 whitespace-nowrap">Situação</th>
                      <th className="px-3 sm:px-5 py-3 text-right whitespace-nowrap">Ações</th>
                    </tr>
                  </thead>
                  <tbody data-testid="detail-contracts-list">
                    {contractsMarked.map((c, i) => (
                      <tr key={c.session_id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                        <td className="px-3 sm:px-5 py-4 font-semibold text-slate-900 whitespace-nowrap">{c.plan_name}</td>
                        <td className="px-3 sm:px-5 py-4 text-slate-600 whitespace-nowrap">
                          {c.duration}{c.active && <span className="text-xs text-slate-400 ml-2">· Válido até {fmtDate(s.membership_end)}</span>}
                        </td>
                        <td className="px-3 sm:px-5 py-4 whitespace-nowrap">
                          {c.active ? (
                            isSuspended ? (
                              <span className="inline-block px-6 sm:px-8 py-2 rounded-lg bg-amber-500 text-white text-xs font-bold uppercase tracking-wider" data-testid={`contract-status-${i}`}>Suspenso</span>
                            ) : (
                              <span className="inline-block px-6 sm:px-8 py-2 rounded-lg bg-emerald-500 text-white text-xs font-bold uppercase tracking-wider" data-testid={`contract-status-${i}`}>Ativo</span>
                            )
                          ) : (
                            <span className="inline-block px-6 sm:px-8 py-2 rounded-lg bg-slate-300 dark:bg-slate-800 text-rose-700 dark:text-rose-400 text-xs font-bold uppercase tracking-wider border-2 border-rose-500" data-testid={`contract-status-${i}`}>Encerrado</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-5 py-4 text-right whitespace-nowrap">
                          <Button size="sm" variant="ghost" onClick={()=>openDelete(c)} className="text-rose-500 hover:bg-rose-50" data-testid={`contract-delete-${i}`}><Trash2 className="w-4 h-4"/></Button>
                        </td>
                      </tr>
                    ))}
                    {contractsMarked.length === 0 && <tr><td colSpan={4} className="px-5 py-12 text-center text-slate-500">Nenhum contrato registrado</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "financeiro" && (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-3 sm:px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-2 flex-wrap"><h3 className="font-heading text-lg font-bold text-slate-900">Histórico financeiro</h3>{payPage && <span className="text-xs text-slate-500" data-testid="detail-payments-total">{payPage.total} pagamento(s)</span>}</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                      <th className="px-3 sm:px-5 py-3 whitespace-nowrap">Data</th><th className="px-3 sm:px-5 py-3 whitespace-nowrap">Método</th><th className="px-3 sm:px-5 py-3 whitespace-nowrap">Origem</th><th className="px-3 sm:px-5 py-3 text-right whitespace-nowrap">Valor</th><th className="px-3 sm:px-5 py-3 whitespace-nowrap">Status</th><th className="px-3 sm:px-5 py-3 text-right whitespace-nowrap">Ações</th>
                    </tr>
                  </thead>
                  <tbody data-testid="detail-payments-list">
                    {(payPage?.items || []).map(p => {
                      const src = p.source || "sistema";
                      const srcLabel = SOURCE_LABEL[src] || "Sistema";
                      const srcColor = SOURCE_COLOR[src] || "#64748b";
                      return (
                      <tr key={p.session_id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-3 sm:px-5 py-3 text-slate-700 whitespace-nowrap">{new Date(p.created_at).toLocaleString("pt-BR")}</td>
                        <td className="px-3 sm:px-5 py-3 whitespace-nowrap"><span className="text-xs px-2 py-1 rounded-full font-semibold" style={{background:(METHOD_COLOR[p.kind]||"#94a3b8")+"1a", color: METHOD_COLOR[p.kind]||"#475569"}}>{METHOD_LABEL[p.kind] || p.kind}</span></td>
                        <td className="px-3 sm:px-5 py-3 whitespace-nowrap"><span className="text-xs px-2 py-1 rounded-full font-semibold" style={{background: srcColor+"1a", color: srcColor}} data-testid={`detail-payment-source-${p.session_id}`}>{srcLabel}</span></td>
                        <td className={`px-3 sm:px-5 py-3 text-right font-mono font-bold whitespace-nowrap ${p.payment_status === "paid" ? "text-emerald-600" : "text-slate-600"}`}>R$ {((p.amount || 0)/100).toFixed(2)}</td>
                        <td className="px-3 sm:px-5 py-3 whitespace-nowrap">{(() => { const late = p.payment_status === "pending" && p.source === "nextfit" && (p.due_date || String(p.created_at).slice(0, 10)) < new Date().toISOString().slice(0, 10); return <span className={`text-xs px-2 py-1 rounded-full font-semibold ${p.payment_status === "paid" ? "bg-emerald-50 text-emerald-700" : late ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`} data-testid={`detail-payment-status-${p.session_id}`}>{late ? "Em atraso" : (STATUS_LABEL[p.payment_status] || p.payment_status)}</span>; })()}</td>
                        <td className="px-3 sm:px-5 py-3 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-1">
                            {p.payment_status === "paid" && (
                              <>
                                <Button size="sm" variant="ghost" onClick={()=>printThermalReceipt(p)} className="text-slate-700 hover:bg-slate-100" title="Imprimir recibo térmico 80mm" data-testid={`detail-print-receipt-${p.session_id}`}><Printer className="w-4 h-4"/></Button>
                                <Button size="sm" variant="ghost" onClick={()=>sendReceiptWhatsApp(p)} className="text-emerald-600 hover:bg-emerald-50" title="Enviar recibo pelo WhatsApp" data-testid={`detail-wa-receipt-${p.session_id}`}><MessageCircle className="w-4 h-4"/></Button>
                                <Button size="sm" variant="ghost" onClick={()=>openDelete(p)} className="text-rose-500 hover:bg-rose-50" title="Excluir pagamento" data-testid="detail-delete-payment"><Trash2 className="w-4 h-4"/></Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );})}
                    {payPage && payPage.items.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-500">Nenhum pagamento registrado</td></tr>}
                  </tbody>
                </table>
              </div>
              {payPage && payPage.pages > 1 && (
                <div className="px-3 sm:px-5 py-3 flex items-center justify-end gap-2 text-sm" data-testid="detail-payments-pager">
                  <Button size="sm" variant="outline" disabled={payPage.page <= 1} onClick={() => loadPayments(payPage.page - 1)} data-testid="detail-payments-prev">Anterior</Button>
                  <span data-testid="detail-payments-page">Página {payPage.page} de {payPage.pages}</span>
                  <Button size="sm" variant="outline" disabled={payPage.page >= payPage.pages} onClick={() => loadPayments(payPage.page + 1)} data-testid="detail-payments-next">Próxima</Button>
                </div>
              )}
            </div>
          )}

          {tab === "treinos" && (
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="font-heading text-lg font-bold text-slate-900">Fichas de treino</h3>
                  <p className="text-sm text-slate-500">{data.workouts.length} treino{data.workouts.length !== 1 ? "s" : ""} atribuído{data.workouts.length !== 1 ? "s" : ""}</p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={openWkManual} className="bg-rose-600 hover:bg-rose-700 font-bold uppercase" data-testid="detail-wk-manual-button"><Plus className="w-4 h-4 mr-2"/>Novo treino</Button>
                  <Button onClick={openWkCopy} variant="outline" className="border-rose-600 text-rose-600 hover:bg-rose-50 font-bold uppercase" data-testid="detail-wk-copy-button"><Copy className="w-4 h-4 mr-2"/>Copiar existente</Button>
                  <Button onClick={()=>setWkAIOpen(true)} variant="outline" className="border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-bold uppercase" data-testid="detail-wk-ai-button"><Sparkles className="w-4 h-4 mr-2"/>Gerar com IA</Button>
                </div>
              </div>

              {data.workouts.length === 0 ? (
                <div className="bg-white rounded-xl border border-dashed border-slate-300 py-12 text-center">
                  <Dumbbell className="w-12 h-12 text-slate-300 mx-auto mb-2"/>
                  <p className="text-slate-500 font-semibold">Nenhum treino atribuído ainda</p>
                  <p className="text-xs text-slate-400 mt-1">Crie manualmente ou gere com IA (GPT-5.4)</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="detail-workouts-list">
                  {data.workouts.map(w => {
                    const exp = wkExpanded[w.id];
                    return (
                      <div key={w.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                        <div className="p-4 flex items-center gap-3 flex-wrap">
                          <button onClick={()=>toggleWk(w.id)} className="p-1 rounded hover:bg-slate-100 shrink-0">
                            {exp ? <ChevronDown className="w-4 h-4"/> : <ChevronRight className="w-4 h-4"/>}
                          </button>
                          <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center shrink-0"><Dumbbell className="w-5 h-5 text-rose-600"/></div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="font-heading font-bold text-slate-900 truncate">{w.title}</div>
                              {w.generated_by_ai && <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 font-bold uppercase">IA</span>}
                            </div>
                            <div className="text-xs text-slate-500 truncate">{w.goal || "Sem objetivo"} · {w.days.length} ficha{w.days.length !== 1 ? "s" : ""} · {new Date(w.created_at).toLocaleDateString("pt-BR")}</div>
                          </div>
                          <Button variant="ghost" size="icon" onClick={()=>printWorkout(w)} className="text-slate-600 hover:bg-slate-100 shrink-0" title="Imprimir/PDF" data-testid={`wk-print-${w.id}`}><Printer className="w-4 h-4"/></Button>
                          <Button variant="ghost" size="icon" onClick={()=>openWkDuplicate(w)} className="text-slate-600 hover:bg-slate-100 shrink-0" title="Duplicar aqui" data-testid={`wk-duplicate-${w.id}`}><Copy className="w-4 h-4"/></Button>
                          <Button variant="ghost" size="icon" onClick={()=>saveAsTemplate(w)} disabled={wkTplSaving[w.id]} className="text-amber-600 hover:bg-amber-50 shrink-0" title="Salvar na Biblioteca de Treinos Salvos" data-testid={`wk-save-template-${w.id}`}>{wkTplSaving[w.id] ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}</Button>
                          <Button variant="ghost" size="icon" onClick={()=>openWkDupTo(w)} className="text-emerald-600 hover:bg-emerald-50 shrink-0" title="Copiar para outros alunos" data-testid={`wk-duplicate-to-${w.id}`}><Users className="w-4 h-4"/></Button>
                          <Button variant="ghost" size="icon" onClick={()=>openWkEdit(w)} className="text-sky-600 hover:bg-sky-50 shrink-0" title="Editar" data-testid={`wk-edit-${w.id}`}><Pencil className="w-4 h-4"/></Button>
                          <Button variant="ghost" size="icon" onClick={()=>deleteWorkout(w.id)} className="text-rose-500 hover:bg-rose-50 shrink-0" title="Excluir"><Trash2 className="w-4 h-4"/></Button>
                        </div>
                        {exp && (
                          <div className="border-t border-slate-100 p-4 bg-slate-50 space-y-3">
                            {w.days.map((d, di) => (
                              <div key={di} className="bg-white rounded-lg border border-slate-200 p-3">
                                <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-100">
                                  <span className="w-7 h-7 rounded-lg bg-rose-600 text-white flex items-center justify-center font-heading font-bold text-sm">{d.label}</span>
                                  <span className="font-semibold text-slate-900">{d.focus || "Ficha"}</span>
                                </div>
                                <table className="w-full text-sm">
                                  <thead className="text-xs uppercase tracking-wider text-slate-500">
                                    <tr><th className="text-left pb-1">Exercício</th><th className="text-center pb-1">Séries</th><th className="text-center pb-1">Reps</th><th className="text-center pb-1">Descanso</th><th className="text-center pb-1">Carga</th></tr>
                                  </thead>
                                  <tbody>
                                    {d.exercises.map((e, ei) => (
                                      <tr key={ei} className="border-t border-slate-100">
                                        <td className="py-1.5 text-slate-900 font-medium">
                                          <div className="flex items-center gap-2">
                                            {e.video_url && (
                                              <button onClick={()=>setVideoModal({ url: e.video_url, title: e.name })} className="text-rose-600 hover:bg-rose-50 rounded p-1" data-testid={`play-video-${w.id}-${di}-${ei}`}>
                                                <Play className="w-3.5 h-3.5"/>
                                              </button>
                                            )}
                                            <span>{e.name}</span>
                                          </div>
                                        </td>
                                        <td className="text-center text-slate-700">{e.sets}</td>
                                        <td className="text-center text-slate-700">{e.reps}</td>
                                        <td className="text-center text-slate-700">{e.rest_seconds}s</td>
                                        <td className="text-center text-slate-700">{e.load || "—"}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {tab === "presencas" && canPresencas && <StudentPresencas studentId={studentId}/>}

          {tab === "avaliacoes" && (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center hidden">
              <HeartPulse className="w-12 h-12 text-slate-300 mx-auto mb-3"/>
              <h3 className="font-heading text-lg font-bold text-slate-900">Avaliações Físicas</h3>
              <p className="text-sm text-slate-500 mt-1">Módulo em breve — registrar peso, medidas e evolução do aluno.</p>
            </div>
          )}
          {tab === "servicos" && (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center hidden">
              <Wrench className="w-12 h-12 text-slate-300 mx-auto mb-3"/>
              <h3 className="font-heading text-lg font-bold text-slate-900">Serviços Adicionais</h3>
              <p className="text-sm text-slate-500 mt-1">Módulo em breve — vender serviços avulsos (personal, avaliação, day-use).</p>
            </div>
          )}
        </div>

        {/* Delete confirmation */}
        <Dialog open={delOpen} onOpenChange={setDelOpen}>
          <DialogContent data-testid="detail-delete-dialog">
            <h3 className="font-heading text-lg font-bold text-rose-600 flex items-center gap-2"><Trash2 className="w-5 h-5"/>Excluir venda</h3>
            {delTx && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm mt-3">
                <div className="font-semibold">{delTx.plan_name || (plans.find(p=>p.id===delTx.plan_id)?.name || "Plano")}</div>
                <div className="text-emerald-700 font-mono font-bold">R$ {((delTx.amount||0)/100).toFixed(2)}</div>
              </div>
            )}
            <Textarea rows={2} value={delReason} onChange={(e)=>setDelReason(e.target.value)} placeholder="Motivo da exclusão (obrigatório)" data-testid="detail-delete-reason" className="mt-3"/>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 mt-2">
              Apenas os dias <strong>deste plano</strong> serão revertidos do acesso do aluno. Se ele tiver outro plano ativo, esse continuará valendo.
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={()=>setDelOpen(false)}>Cancelar</Button>
              <Button onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="detail-delete-confirm">Excluir</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Quick Photo Capture */}
        <Dialog open={photoOpen} onOpenChange={(v)=>{ setPhotoOpen(v); if (!v) stopPhotoCam(); }}>
          <DialogContent className="max-w-md" data-testid="detail-photo-dialog">
            <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2"><Camera className="w-5 h-5 text-rose-600"/>Atualizar foto de perfil</h3>
            <p className="text-sm text-slate-500 mt-1">Posicione o rosto centralizado e capture. Se detectarmos o rosto, o descriptor 128D da catraca também será atualizado.</p>
            <div className="mt-4">
              {photoStreaming ? (
                <div className="space-y-3">
                  <div className="relative bg-black rounded-xl overflow-hidden aspect-video">
                    <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" style={{ transform: "scaleX(-1)" }}/>
                    <div className="absolute inset-0 border-4 border-rose-500/30 rounded-xl pointer-events-none"/>
                    <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 text-white text-xs">
                      <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"/>AO VIVO
                    </div>
                  </div>
                  <canvas ref={canvasRef} className="hidden"/>
                  <div className="flex gap-2">
                    <Button onClick={capturePhoto} disabled={photoSaving} className="flex-1 bg-emerald-600 hover:bg-emerald-700 font-bold uppercase" data-testid="detail-photo-capture">
                      <Camera className="w-4 h-4 mr-2"/>{photoSaving ? "Salvando..." : "Capturar"}
                    </Button>
                    <Button variant="outline" onClick={stopPhotoCam} data-testid="detail-photo-cancel"><VideoOff className="w-4 h-4"/></Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-3">
                    <Camera className="w-8 h-8 text-rose-500"/>
                  </div>
                  <p className="text-sm text-slate-600 mb-4">{modelsReady ? "Pronto para capturar" : "Carregando modelos..."}</p>
                  <Button onClick={startPhotoCam} className="bg-rose-600 hover:bg-rose-700 font-bold uppercase" data-testid="detail-photo-start"><Camera className="w-4 h-4 mr-2"/>Abrir câmera</Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Cadastro edit */}
        <Dialog open={cadastroOpen} onOpenChange={setCadastroOpen}>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="detail-cadastro-dialog">
            <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2 mb-1"><ClipboardEdit className="w-5 h-5 text-sky-600"/>Editar cadastro do aluno</h3>
            <p className="text-sm text-slate-500 mb-4">Dados pessoais, endereço e contato de emergência.</p>

            <div className="space-y-5">
              <div className="flex items-center gap-3 pb-2 border-b border-slate-200">
                <div className="relative shrink-0">
                  <div className="w-16 h-16 rounded-full border-2 border-rose-300 overflow-hidden bg-slate-100 flex items-center justify-center">
                    {s.face_photo ? <img src={s.face_photo} alt="" className="w-full h-full object-cover"/> : <User className="w-6 h-6 text-slate-400"/>}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900">Foto de perfil</div>
                  <div className="text-xs text-slate-500">Aparece no header, catraca e ficha impressa</div>
                </div>
                <Button size="sm" onClick={()=>{ setCadastroOpen(false); openPhoto(); }} className="bg-rose-600 hover:bg-rose-700 font-bold uppercase" data-testid="cadastro-photo-button"><Camera className="w-4 h-4 mr-2"/>Alterar foto</Button>
              </div>
              <div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><User className="w-3.5 h-3.5"/>Dados pessoais</div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="md:col-span-2"><Label>Nome completo</Label><Input value={cadastroForm.name||""} onChange={(e)=>setCadastroForm({...cadastroForm,name:e.target.value})} data-testid="cadastro-name"/></div>
                  <div><Label>CPF</Label><Input value={cadastroForm.cpf||""} onChange={(e)=>setCadastroForm({...cadastroForm,cpf:maskCPF(e.target.value)})} placeholder="000.000.000-00" data-testid="cadastro-cpf"/></div>
                  <div><Label>Telefone</Label><Input value={cadastroForm.phone||""} onChange={(e)=>setCadastroForm({...cadastroForm,phone:maskPhone(e.target.value)})} placeholder="(11) 99999-9999" data-testid="cadastro-phone"/></div>
                  <div><Label>Email</Label><Input type="email" value={cadastroForm.email||""} onChange={(e)=>setCadastroForm({...cadastroForm,email:e.target.value})}/></div>
                  <div><Label>Nascimento</Label><Input inputMode="numeric" value={cadastroForm.birthdate||""} onChange={(e)=>setCadastroForm({...cadastroForm,birthdate:maskDate(e.target.value)})} placeholder="DD/MM/AAAA" data-testid="cadastro-birthdate"/></div>
                  <div>
                    <Label>Gênero</Label>
                    <Select value={cadastroForm.gender||""} onValueChange={(v)=>setCadastroForm({...cadastroForm,gender:v})}>
                      <SelectTrigger data-testid="cadastro-gender"><SelectValue placeholder="Selecionar"/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Masculino">Masculino</SelectItem>
                        <SelectItem value="Feminino">Feminino</SelectItem>
                        <SelectItem value="Outro">Outro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Nível</Label>
                    <Select value={cadastroForm.experience_level||"iniciante"} onValueChange={(v)=>setCadastroForm({...cadastroForm,experience_level:v})}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="iniciante">Iniciante</SelectItem>
                        <SelectItem value="intermediario">Intermediário</SelectItem>
                        <SelectItem value="avancado">Avançado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5"/>Endereço</div>
                <div className="grid md:grid-cols-6 gap-3">
                  <div className="md:col-span-2">
                    <Label>CEP {cepLoading && <span className="text-xs text-sky-600 ml-1">buscando...</span>}</Label>
                    <Input value={cadastroForm.zip_code||""} onChange={(e)=>onCepChange(e.target.value)} placeholder="00000-000" data-testid="cadastro-cep"/>
                  </div>
                  <div className="md:col-span-4"><Label>Logradouro</Label><Input value={cadastroForm.address||""} onChange={(e)=>setCadastroForm({...cadastroForm,address:e.target.value})} data-testid="cadastro-address"/></div>
                  <div className="md:col-span-1"><Label>Número</Label><Input value={cadastroForm.address_number||""} onChange={(e)=>setCadastroForm({...cadastroForm,address_number:e.target.value})}/></div>
                  <div className="md:col-span-2"><Label>Complemento</Label><Input value={cadastroForm.address_complement||""} onChange={(e)=>setCadastroForm({...cadastroForm,address_complement:e.target.value})}/></div>
                  <div className="md:col-span-3"><Label>Bairro</Label><Input value={cadastroForm.neighborhood||""} onChange={(e)=>setCadastroForm({...cadastroForm,neighborhood:e.target.value})}/></div>
                  <div className="md:col-span-4"><Label>Cidade</Label><Input value={cadastroForm.city||""} onChange={(e)=>setCadastroForm({...cadastroForm,city:e.target.value})}/></div>
                  <div className="md:col-span-2"><Label>UF</Label><Input maxLength={2} value={cadastroForm.state||""} onChange={(e)=>setCadastroForm({...cadastroForm,state:e.target.value.toUpperCase()})}/></div>
                </div>
              </div>

              <div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5"/>Contato de emergência</div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div><Label>Nome</Label><Input value={cadastroForm.emergency_contact_name||""} onChange={(e)=>setCadastroForm({...cadastroForm,emergency_contact_name:e.target.value})}/></div>
                  <div><Label>Telefone</Label><Input value={cadastroForm.emergency_contact_phone||""} onChange={(e)=>setCadastroForm({...cadastroForm,emergency_contact_phone:maskPhone(e.target.value)})} placeholder="(11) 99999-9999"/></div>
                </div>
              </div>

              <div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Objetivo & Observações</div>
                <div className="grid gap-3">
                  <div><Label>Objetivo do aluno</Label><Textarea rows={2} value={cadastroForm.goal||""} onChange={(e)=>setCadastroForm({...cadastroForm,goal:e.target.value})}/></div>
                  <div><Label>Observações internas</Label><Textarea rows={3} value={cadastroForm.notes||""} onChange={(e)=>setCadastroForm({...cadastroForm,notes:e.target.value})} placeholder="Restrições médicas, preferências, notas do atendimento"/></div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-slate-200 sticky bottom-0 bg-white">
              <Button variant="outline" onClick={()=>setCadastroOpen(false)}>Cancelar</Button>
              <Button onClick={saveCadastro} disabled={cadastroSaving} className="bg-sky-500 hover:bg-sky-600 font-bold uppercase" data-testid="cadastro-save"><Save className="w-4 h-4 mr-2"/>{cadastroSaving ? "Salvando..." : "Salvar cadastro"}</Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={rmDaysOpen} onOpenChange={setRmDaysOpen}>
          <DialogContent className="max-w-md" data-testid="detail-remove-days-dialog">
            <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2 mb-1"><CalendarMinus className="w-5 h-5 text-rose-600"/>Retirar dias do plano</h3>
            <p className="text-sm text-slate-500 mb-4">Diminui o vencimento do aluno. Apenas administradores. Fica registrado na Auditoria.</p>
            <div className="space-y-3">
              <div>
                <Label>Quantidade de dias</Label>
                <Input type="number" min="1" max="3650" value={rmDaysForm.days} onChange={(e)=>setRmDaysForm({...rmDaysForm, days: parseInt(e.target.value)||0})} className="w-28 mt-1" data-testid="detail-remove-days-input"/>
              </div>
              <div>
                <Label>Motivo *</Label>
                <Textarea rows={2} value={rmDaysForm.reason} onChange={(e)=>setRmDaysForm({...rmDaysForm, reason: e.target.value})} placeholder="Ex: dias lançados por engano" data-testid="detail-remove-days-reason"/>
              </div>
              {s.membership_end && rmDaysForm.days > 0 && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm" data-testid="detail-remove-days-preview">
                  <div className="text-xs text-rose-700 font-semibold uppercase tracking-wider mb-1">Nova validade</div>
                  <div className="font-heading font-bold text-rose-900">
                    {fmtDate(s.membership_end)} → {fmtDate(new Date(new Date(s.membership_end).getTime() - rmDaysForm.days * 86400000).toISOString())}
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={()=>setRmDaysOpen(false)}>Cancelar</Button>
              <Button onClick={doRemoveDays} disabled={rmDaysSaving || !s.membership_end} className="bg-rose-600 hover:bg-rose-700 font-bold uppercase text-white" data-testid="detail-remove-days-confirm"><CalendarMinus className="w-4 h-4 mr-2"/>{rmDaysSaving ? "Salvando..." : "Retirar dias"}</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Days (Admin only) */}
        <Dialog open={addDaysOpen} onOpenChange={setAddDaysOpen}>
          <DialogContent className="max-w-md" data-testid="detail-add-days-dialog">
            <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2 mb-1"><CalendarPlus className="w-5 h-5 text-emerald-600"/>Acrescentar dias ao plano</h3>
            <p className="text-sm text-slate-500 mb-4">Bonificação gratuita — soma dias à mensalidade atual do aluno. Apenas administradores.</p>
            <div className="space-y-3">
              <div>
                <Label>Quantidade de dias</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input type="number" min="1" max="3650" value={addDaysForm.days} onChange={(e)=>setAddDaysForm({...addDaysForm, days: parseInt(e.target.value)||0})} className="w-28" data-testid="detail-add-days-input"/>
                  <div className="flex gap-1">
                    {[7, 15, 30, 60, 90].map(n => (
                      <Button key={n} type="button" size="sm" variant="outline" onClick={()=>setAddDaysForm({...addDaysForm, days: n})} className="text-xs">+{n}d</Button>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <Label>Motivo (recomendado)</Label>
                <Textarea rows={2} value={addDaysForm.reason} onChange={(e)=>setAddDaysForm({...addDaysForm, reason: e.target.value})} placeholder="Ex: bonificação de indicação, compensação por manutenção, cortesia"/>
              </div>
              {s.membership_end && addDaysForm.days > 0 && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm">
                  <div className="text-xs text-emerald-700 font-semibold uppercase tracking-wider mb-1">Nova validade</div>
                  <div className="font-heading font-bold text-emerald-900">
                    {fmtDate(s.membership_end)} → {(() => {
                      try {
                        const cur = new Date(s.membership_end);
                        const base = cur > new Date() ? cur : new Date();
                        const nd = new Date(base.getTime() + addDaysForm.days * 86400000);
                        return nd.toLocaleDateString("pt-BR");
                      } catch { return "—"; }
                    })()}
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={()=>setAddDaysOpen(false)}>Cancelar</Button>
              <Button onClick={doAddDays} disabled={addDaysSaving} className="bg-emerald-600 hover:bg-emerald-700 font-bold uppercase" data-testid="detail-add-days-confirm"><CalendarPlus className="w-4 h-4 mr-2"/>{addDaysSaving ? "Salvando..." : "Adicionar dias"}</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Change default unit */}
        <Dialog open={unitOpen} onOpenChange={setUnitOpen}>
          <DialogContent className="max-w-md" data-testid="detail-unit-dialog">
            <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2 mb-1"><Building2 className="w-5 h-5 text-rose-600"/>Trocar unidade padrão</h3>
            <p className="text-sm text-slate-500 mb-4">
              A unidade padrão define onde o aluno é gerenciado (visível para recepção/personal daquela unidade). O acesso à catraca continua funcionando em qualquer unidade cadastrada.
            </p>
            <div className="space-y-3">
              <div>
                <Label>Unidade</Label>
                <Select value={unitId} onValueChange={setUnitId}>
                  <SelectTrigger data-testid="detail-unit-select"><SelectValue placeholder="Selecionar"/></SelectTrigger>
                  <SelectContent>
                    {unitsList.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {unit && unitId && unitId !== s.unit_id && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                  De <strong>{unit.name}</strong> → <strong>{unitsList.find(u => u.id === unitId)?.name}</strong>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={()=>setUnitOpen(false)}>Cancelar</Button>
              <Button onClick={doChangeUnit} disabled={unitSaving || !unitId} className="bg-rose-600 hover:bg-rose-700 font-bold uppercase text-white" data-testid="detail-unit-save">
                <Building2 className="w-4 h-4 mr-2"/>{unitSaving ? "Salvando..." : "Salvar unidade"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Copy workout FROM another student or template library */}
        <Dialog open={wkCopyOpen} onOpenChange={setWkCopyOpen}>
          <DialogContent className="max-w-2xl" data-testid="detail-wk-copy-dialog">
            <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2 mb-1"><Copy className="w-5 h-5 text-rose-600"/>Criar treino a partir de outro</h3>
            <p className="text-sm text-slate-500 mb-3">Escolha um treino existente para pré-carregar todos os dias e exercícios no formulário. Você poderá revisar antes de salvar para <strong>{s.name}</strong>.</p>

            <div className="flex gap-2 mb-3 border-b border-slate-200">
              <button onClick={()=>setWkCopyTab("students")} className={`px-3 py-2 text-sm font-heading font-bold uppercase tracking-wider border-b-2 -mb-px ${wkCopyTab === "students" ? "border-rose-600 text-rose-600" : "border-transparent text-slate-500"}`} data-testid="wk-copy-tab-students">
                <Users className="w-4 h-4 inline mr-1.5"/>De outro aluno <span className="ml-1 text-xs text-slate-400">({wkCopyList.length})</span>
              </button>
              <button onClick={()=>setWkCopyTab("templates")} className={`px-3 py-2 text-sm font-heading font-bold uppercase tracking-wider border-b-2 -mb-px ${wkCopyTab === "templates" ? "border-amber-600 text-amber-600" : "border-transparent text-slate-500"}`} data-testid="wk-copy-tab-templates">
                <Save className="w-4 h-4 inline mr-1.5"/>Biblioteca <span className="ml-1 text-xs text-slate-400">({wkCopyTemplates.length})</span>
              </button>
            </div>

            <Input placeholder="Buscar por título, objetivo ou aluno" value={wkCopySearch} onChange={(e)=>setWkCopySearch(e.target.value)} className="mb-3" data-testid="wk-copy-search"/>
            {wkCopyTab === "templates" && (
              <div className="flex flex-wrap gap-1.5 mb-3" data-testid="wk-copy-category-filter">
                {["Todas", ...Array.from(new Set(wkCopyTemplates.map(t => t.category || "Outros")))].map(c => (
                  <button key={c} type="button" onClick={()=>setWkCopyCat(c)} data-testid={`wk-copy-cat-${c}`}
                    className={`text-xs px-2.5 py-1 rounded-full font-semibold ${wkCopyCat === c ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-700"}`}>{c}</button>
                ))}
              </div>
            )}

            <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="wk-copy-list">
              {wkCopyLoading && <div className="p-6 text-center text-slate-400 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2"/>Carregando...</div>}
              {!wkCopyLoading && (() => {
                const q = wkCopySearch.trim().toLowerCase();
                const src = wkCopyTab === "students" ? wkCopyList : wkCopyTemplates;
                const filtered = src.filter(w => {
                  if (wkCopyTab === "templates" && wkCopyCat !== "Todas" && (w.category || "Outros") !== wkCopyCat) return false;
                  if (!q) return true;
                  const sname = wkCopyStudentsMap[w.student_id] || "";
                  return (w.title || "").toLowerCase().includes(q) || (w.goal || "").toLowerCase().includes(q) || sname.toLowerCase().includes(q);
                });
                if (filtered.length === 0) return <div className="p-6 text-center text-slate-400 text-sm">{wkCopyTab === "templates" ? "Nenhum template salvo ainda. Salve um treino como template pelo ícone" : "Nenhum treino encontrado."}</div>;
                return filtered.slice(0, 100).map(w => (
                  <button key={w.id} type="button" onClick={()=>applyWkFromSource(w)} className="w-full text-left px-3 py-3 hover:bg-rose-50 flex items-center justify-between gap-3" data-testid={`wk-copy-source-${w.id}`}>
                    <div className="min-w-0">
                      <div className="font-heading font-bold text-slate-900 truncate flex items-center gap-2">
                        {w.title}
                        {w.generated_by_ai && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 font-bold uppercase">IA</span>}
                        {w.is_template && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold uppercase">{w.category || "Outros"}</span>}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {w.goal || "Sem objetivo"} · {(w.days || []).length} dia(s) · {new Date(w.created_at).toLocaleDateString("pt-BR")}
                        {wkCopyTab === "students" && wkCopyStudentsMap[w.student_id] && <> · aluno: <strong>{wkCopyStudentsMap[w.student_id]}</strong></>}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 shrink-0"/>
                  </button>
                ));
              })()}
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={()=>setWkCopyOpen(false)}>Fechar</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Mercado Pago Pix — venda */}
        {sellPlan && (
          <MpPixDialog
            open={mpSellOpen}
            onOpenChange={setMpSellOpen}
            plan={plans.find(x => x.id === sellPlan)}
            auth={api}
            createPayload={{ student_id: studentId, plan_id: sellPlan, payer_email: s.email }}
            onPaid={() => { setSellPlan(""); load(); onUpdate && onUpdate(); toast.success("Plano vendido via Pix MP"); }}
            title="Vender plano — Pix Mercado Pago"
          />
        )}
        {/* Mercado Pago Pix — renovação */}
        {s.plan_id && (
          <MpPixDialog
            open={mpRenewOpen}
            onOpenChange={setMpRenewOpen}
            plan={plans.find(x => x.id === s.plan_id)}
            auth={api}
            createPayload={{ student_id: studentId, plan_id: s.plan_id, payer_email: s.email }}
            onPaid={() => { load(); onUpdate && onUpdate(); toast.success("Mensalidade renovada via Pix MP"); }}
            title="Renovar plano — Pix Mercado Pago"
          />
        )}

        {/* Duplicate workout to other students */}
        <Dialog open={wkDupOpen} onOpenChange={setWkDupOpen}>
          <DialogContent className="max-w-lg" data-testid="detail-wk-dup-dialog">
            <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2 mb-1"><Users className="w-5 h-5 text-emerald-600"/>Copiar treino para outros alunos</h3>
            <p className="text-sm text-slate-500 mb-4">
              Treino: <strong>{wkDupWorkout?.title}</strong> — selecione um ou mais alunos que receberão uma cópia idêntica desta ficha.
            </p>
            <div className="space-y-3">
              <Input placeholder="Buscar por nome ou CPF" value={wkDupSearch} onChange={(e)=>setWkDupSearch(e.target.value)} data-testid="wk-dup-search"/>
              <div className="max-h-60 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="wk-dup-target-list">
                {allStudents
                  .filter(x => x.id !== studentId)
                  .filter(x => {
                    const q = wkDupSearch.trim().toLowerCase();
                    if (!q) return true;
                    return x.name?.toLowerCase().includes(q) || (x.cpf || "").includes(q.replace(/\D/g, ""));
                  })
                  .slice(0, 60)
                  .map(x => {
                    const selected = wkDupTargets.includes(x.id);
                    return (
                      <button key={x.id} type="button" onClick={()=>toggleDupTarget(x.id)} className={`w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 flex items-center justify-between ${selected ? "bg-emerald-100" : ""}`} data-testid={`wk-dup-target-${x.id}`}>
                        <div>
                          <div className="font-semibold text-slate-800">{x.name}</div>
                          <div className="text-xs text-slate-500">CPF: {maskCPF(x.cpf || "")}</div>
                        </div>
                        {selected && <CheckCircle2 className="w-5 h-5 text-emerald-600"/>}
                      </button>
                    );
                  })}
                {allStudents.length === 0 && <div className="p-3 text-center text-slate-400 text-sm">Carregando alunos...</div>}
              </div>
              {wkDupTargets.length > 0 && (
                <div className="text-xs text-emerald-700 font-semibold bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                  {wkDupTargets.length} aluno(s) selecionado(s) — cada um receberá uma nova ficha idêntica
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={()=>setWkDupOpen(false)}>Cancelar</Button>
              <Button onClick={doWkDupTo} disabled={wkDupSaving || wkDupTargets.length === 0} className="bg-emerald-600 hover:bg-emerald-700 font-bold uppercase text-white" data-testid="wk-dup-confirm">
                <Copy className="w-4 h-4 mr-2"/>{wkDupSaving ? "Copiando..." : `Copiar para ${wkDupTargets.length || ""} aluno(s)`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Transfer days */}
        <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
          <DialogContent className="max-w-lg" data-testid="detail-transfer-dialog">
            <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2 mb-1"><ArrowRightLeft className="w-5 h-5 text-sky-600"/>Transferir dias para outro aluno</h3>
            <p className="text-sm text-slate-500 mb-4">
              Dias restantes de <strong>{s.name}</strong>: <span className="text-sky-700 font-bold">{Math.max(0, Math.floor(((new Date(s.membership_end || 0)) - new Date()) / 86400000))} dia(s)</span>. O aluno origem passará a expirar agora e o destino receberá os dias somados.
            </p>
            <div className="space-y-3">
              <div>
                <Label>Aluno destino</Label>
                <Input placeholder="Buscar por nome ou CPF" value={transferSearch} onChange={(e)=>setTransferSearch(e.target.value)} className="mb-2" data-testid="transfer-search"/>
                <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {allStudents
                    .filter(x => x.id !== studentId)
                    .map(x => (
                      <button key={x.id} type="button" onClick={()=>setTransferForm({...transferForm, target_student_id: x.id})} className={`w-full text-left px-3 py-2 text-sm hover:bg-sky-50 flex items-center justify-between ${transferForm.target_student_id === x.id ? "bg-sky-100" : ""}`} data-testid={`transfer-target-${x.id}`}>
                        <div>
                          <div className="font-semibold text-slate-800">{x.name}</div>
                          <div className="text-xs text-slate-500">CPF: {maskCPF(x.cpf || "")} · Vence: {fmtDate(x.membership_end)}</div>
                        </div>
                        {transferForm.target_student_id === x.id && <CheckCircle2 className="w-5 h-5 text-sky-600"/>}
                      </button>
                    ))}
                  {allStudents.filter(x => x.id !== studentId).length === 0 && <div className="p-3 text-center text-slate-400 text-sm" data-testid="transfer-no-results">Nenhum aluno encontrado</div>}
                </div>
              </div>
              <div>
                <Label>Justificativa da transferência *</Label>
                <Textarea rows={3} value={transferForm.reason} onChange={(e)=>setTransferForm({...transferForm, reason: e.target.value})} placeholder="Ex: Aluno cancelou plano e transferiu para o irmão / Erro de cadastro corrigido" data-testid="transfer-reason"/>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={()=>setTransferOpen(false)}>Cancelar</Button>
              <Button onClick={doTransfer} disabled={transferSaving || !transferForm.target_student_id || !transferForm.reason.trim()} className="bg-sky-600 hover:bg-sky-700 font-bold uppercase text-white" data-testid="transfer-confirm">
                <ArrowRightLeft className="w-4 h-4 mr-2"/>{transferSaving ? "Transferindo..." : "Transferir dias"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Suspend */}
        <Dialog open={suspendOpen} onOpenChange={setSuspendOpen}>
          <DialogContent className="max-w-md" data-testid="detail-suspend-dialog">
            <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2 mb-1"><PauseCircle className="w-5 h-5 text-amber-600"/>Suspender aluno</h3>
            <p className="text-sm text-slate-500 mb-4">
              Os dias restantes ficarão guardados até que a suspensão seja retirada. Enquanto suspenso, o aluno fica bloqueado na catraca.
              <br/>Dias que serão guardados: <strong className="text-amber-700">{Math.max(0, Math.floor(((new Date(s.membership_end || 0)) - new Date()) / 86400000))} dia(s)</strong>
            </p>
            <div className="space-y-3">
              <div>
                <Label>Motivo</Label>
                <Select value={suspendForm.category} onValueChange={(v)=>setSuspendForm({...suspendForm, category: v})}>
                  <SelectTrigger data-testid="suspend-category"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="saude"><span className="flex items-center gap-2"><HeartHandshake className="w-4 h-4 text-rose-500"/>Motivo de saúde</span></SelectItem>
                    <SelectItem value="outro">Outro motivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Detalhes / observações *</Label>
                <Textarea rows={3} value={suspendForm.reason} onChange={(e)=>setSuspendForm({...suspendForm, reason: e.target.value})} placeholder="Ex: Aluno se recuperando de cirurgia / Viagem prolongada / Atestado médico anexo" data-testid="suspend-reason"/>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={()=>setSuspendOpen(false)}>Cancelar</Button>
              <Button onClick={doSuspend} disabled={suspendSaving || !suspendForm.reason.trim()} className="bg-amber-600 hover:bg-amber-700 font-bold uppercase text-white" data-testid="suspend-confirm">
                <PauseCircle className="w-4 h-4 mr-2"/>{suspendSaving ? "Suspendendo..." : "Suspender aluno"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Portal QR Code */}
        <Dialog open={qrOpen} onOpenChange={setQrOpen}>
          <DialogContent className="max-w-md" data-testid="portal-qr-dialog">
            <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2 mb-2"><QrCode className="w-5 h-5 text-emerald-600"/>QR Code do Portal do Aluno</h3>
            <p className="text-sm text-slate-500 mb-4">
              Peça ao <strong>{s.name}</strong> escanear com a câmera do celular para abrir o portal já com o CPF preenchido.
            </p>
            {(() => {
              const url = `${window.location.origin}/portal/login?cpf=${(s.cpf||"").replace(/\D/g,"")}`;
              const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&margin=10&data=${encodeURIComponent(url)}`;
              const doPrint = () => {
                const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>QR Portal — ${s.name}</title>
                  <style>@page{size:80mm auto;margin:0}body{font-family:Courier New,monospace;width:80mm;margin:0;padding:8mm 4mm;text-align:center}
                  h1{font-size:14px;letter-spacing:2px;margin:0 0 4px}.sub{font-size:10px;margin:2px 0}
                  img{width:70mm;height:70mm}.name{font-weight:900;font-size:12px;text-transform:uppercase;margin-top:8px}
                  .cpf{font-size:11px;letter-spacing:1px;margin-top:2px}.link{font-size:8px;margin-top:6px;word-break:break-all}
                  .div{border-top:1px dashed #000;margin:6px 0}
                  @media print{body{padding:4mm 3mm}}</style></head><body>
                  <h1>AMERICAN FIT</h1><div class="sub">Portal do Aluno</div><div class="div"></div>
                  <img src="${qrUrl}" alt="QR"/>
                  <div class="name">${s.name}</div>${s.cpf ? `<div class="cpf">${maskCPF(s.cpf)}</div>` : ""}
                  <div class="div"></div><div class="sub">Escaneie e faça login</div><div class="link">${url}</div>
                  <script>window.onload=()=>setTimeout(()=>window.print(),300);</script></body></html>`;
                const w = window.open("", "_blank", "width=400,height=700");
                if (!w) return toast.error("Popup bloqueado");
                w.document.write(html); w.document.close();
              };
              const shareWa = () => {
                if (!s.phone) return toast.error("Aluno sem telefone");
                const phone = s.phone.replace(/\D/g, "");
                const msg = `Olá ${s.name.split(" ")[0]}! 💪%0A%0AAcesse o portal da American Fit com seu CPF já preenchido:%0A${url}%0A%0AVocê pode ver seu treino, evolução, dieta com IA e muito mais! 🚀`;
                window.open(`https://wa.me/55${phone}?text=${msg}`, "_blank");
              };
              return (
                <div className="text-center space-y-3">
                  <img src={qrUrl} alt="QR Portal" className="mx-auto w-64 h-64 border-4 border-slate-100 rounded-lg" data-testid="portal-qr-img"/>
                  <div className="p-2 bg-slate-50 rounded-lg text-xs font-mono break-all text-slate-600" data-testid="portal-qr-url">{url}</div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={()=>{navigator.clipboard.writeText(url); toast.success("Link copiado");}} className="flex-1" data-testid="portal-qr-copy"><Copy className="w-4 h-4 mr-1"/>Copiar link</Button>
                    <Button onClick={shareWa} disabled={!s.phone} className="flex-1 bg-emerald-600 hover:bg-emerald-700 font-bold uppercase text-white" data-testid="portal-qr-wa"><MessageCircle className="w-4 h-4 mr-1"/>WhatsApp</Button>
                    <Button onClick={doPrint} className="flex-1 bg-slate-900 hover:bg-black font-bold uppercase text-white" data-testid="portal-qr-print"><Printer className="w-4 h-4 mr-1"/>Imprimir</Button>
                  </div>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Portal password */}
        <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
          <DialogContent className="max-w-md">
            <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2 mb-3"><KeyRound className="w-5 h-5 text-sky-600"/>{s.has_portal_password ? "Alterar senha do portal do aluno" : "Definir senha do portal do aluno"}</h3>
            {s.has_portal_password && <div className="mb-3 p-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800" data-testid="portal-password-existing-note">Este aluno já tem senha no portal. A nova senha substitui a atual — a antiga deixa de funcionar.</div>}
            <div className="mb-3 p-3 rounded-lg bg-sky-50 border border-sky-200 text-sm">
              <div className="text-slate-700">O aluno entra no Portal usando:</div>
              <div className="mt-1"><span className="text-xs uppercase font-bold text-slate-500">CPF:</span> <strong className="font-mono text-slate-900">{s.cpf || "— sem CPF cadastrado —"}</strong></div>
              <div><span className="text-xs uppercase font-bold text-slate-500">Senha:</span> {s.has_portal_password ? "a nova senha que você digitar aqui" : "a que você definir aqui"}</div>
              <div className="text-[11px] text-slate-500 mt-1 italic">(O login do portal é por CPF, não por e-mail)</div>
            </div>
            <Label>Nova senha</Label>
            <Input type="text" value={newPwd} onChange={(e)=>setNewPwd(e.target.value)} placeholder="mínimo 4 caracteres" data-testid="portal-password-input"/>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={()=>{setPwdOpen(false); setNewPwd("");}}>Cancelar</Button>
              <Button onClick={setPortalPassword} className="bg-sky-500 hover:bg-sky-600 font-bold uppercase" data-testid="portal-password-save"><Save className="w-4 h-4 mr-2"/>Salvar</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Workout Manual */}
        <Dialog open={wkManualOpen} onOpenChange={setWkManualOpen}>
          <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="wk-manual-dialog">
            <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2 mb-4"><Dumbbell className="w-5 h-5 text-rose-600"/>{wkEditingId ? "Editar treino" : "Novo treino"} — {s.name}</h3>
            {wkManualForm && (
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-3">
                  <div><Label>Título</Label><Input value={wkManualForm.title} onChange={(e)=>setWkManualForm({...wkManualForm,title:e.target.value})} data-testid="wk-title"/></div>
                  <div><Label>Objetivo</Label><Input value={wkManualForm.goal} onChange={(e)=>setWkManualForm({...wkManualForm,goal:e.target.value})} placeholder="Hipertrofia, emagrecimento..."/></div>
                </div>
                {wkManualForm.days.map((d, di) => (
                  <div key={di} className="rounded-xl border-2 border-slate-200 p-4 bg-slate-50">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <span className="w-9 h-9 rounded-lg bg-rose-600 text-white flex items-center justify-center font-heading font-bold">{d.label}</span>
                      <Input value={d.focus} onChange={(e)=>updateDay(di, "focus", e.target.value)} placeholder="Foco (ex: Peito e tríceps)" className="flex-1 min-w-[200px]" data-testid={`wk-day-focus-${di}`}/>
                      {wkManualForm.days.length > 1 && <Button size="sm" variant="ghost" onClick={()=>removeDay(di)} className="text-rose-500"><Trash2 className="w-4 h-4"/></Button>}
                    </div>
                    <div className="space-y-2">
                      {d.exercises.map((e, ei) => (
                        <div key={ei} className="grid grid-cols-12 gap-2 bg-white rounded-lg p-2 border border-slate-200">
                          <Input placeholder="Exercício" value={e.name} onChange={(ev)=>updateExercise(di, ei, "name", ev.target.value)} className="col-span-3" data-testid={`wk-ex-name-${di}-${ei}`}/>
                          <Input type="number" placeholder="Séries" value={e.sets} onChange={(ev)=>updateExercise(di, ei, "sets", parseInt(ev.target.value)||0)} className="col-span-1"/>
                          <Input placeholder="Reps" value={e.reps} onChange={(ev)=>updateExercise(di, ei, "reps", ev.target.value)} className="col-span-1"/>
                          <Input type="number" placeholder="Desc(s)" value={e.rest_seconds} onChange={(ev)=>updateExercise(di, ei, "rest_seconds", parseInt(ev.target.value)||0)} className="col-span-1"/>
                          <Input placeholder="Carga" value={e.load} onChange={(ev)=>updateExercise(di, ei, "load", ev.target.value)} className="col-span-2"/>
                          <Input placeholder="Notas" value={e.notes} onChange={(ev)=>updateExercise(di, ei, "notes", ev.target.value)} className="col-span-2"/>
                          <Button type="button" variant={e.video_url ? "default" : "outline"} size="sm" onClick={()=>openVideoPicker(di, ei)} className={`col-span-1 ${e.video_url ? "bg-rose-600 hover:bg-rose-700" : ""}`} data-testid={`wk-ex-video-${di}-${ei}`}><Video className="w-4 h-4"/></Button>
                          <Button variant="ghost" size="icon" onClick={()=>removeExercise(di, ei)} className="col-span-1 text-rose-400" disabled={d.exercises.length===1}><Trash2 className="w-4 h-4"/></Button>
                        </div>
                      ))}
                      <Button size="sm" variant="outline" onClick={()=>addExercise(di)} data-testid={`wk-add-ex-${di}`} className="border-dashed"><Plus className="w-4 h-4 mr-1"/>Adicionar exercício</Button>
                    </div>
                  </div>
                ))}
                <Button variant="outline" onClick={addDay} className="w-full border-dashed border-slate-400" data-testid="wk-add-day"><Plus className="w-4 h-4 mr-2"/>Adicionar ficha (B, C, D...)</Button>
                <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                  <Button variant="outline" onClick={()=>setWkManualOpen(false)}>Cancelar</Button>
                  <Button onClick={saveWkManual} disabled={wkSaving} className="bg-rose-600 hover:bg-rose-700 font-bold uppercase" data-testid="wk-manual-save"><Save className="w-4 h-4 mr-2"/>{wkSaving ? "Salvando..." : (wkEditingId ? "Atualizar treino" : "Salvar treino")}</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Video Player Modal */}
        <Dialog open={!!videoModal} onOpenChange={(v)=>!v && setVideoModal(null)}>
          <DialogContent className="max-w-3xl" data-testid="video-player-modal">
            <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2 mb-3"><Play className="w-5 h-5 text-rose-600"/>{videoModal?.title}</h3>
            {videoModal && (isYouTube(videoModal.url) ? (
              <div className="aspect-video rounded-lg overflow-hidden bg-black">
                <iframe src={videoModal.url} title={videoModal.title} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen/>
              </div>
            ) : (
              <video src={videoModal.url} controls autoPlay className="w-full rounded-lg bg-black" style={{maxHeight: "70vh"}}/>
            ))}
          </DialogContent>
        </Dialog>

        {/* Video Picker */}
        <Dialog open={videoPickerOpen} onOpenChange={setVideoPickerOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="video-picker-dialog">
            <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2 mb-3"><Video className="w-5 h-5 text-rose-600"/>Anexar vídeo ao exercício</h3>
            <div className="flex gap-1 border-b border-slate-200 mb-4">
              {[
                { key: "library", label: "Biblioteca", icon: SearchIcon },
                { key: "youtube", label: "YouTube", icon: Youtube },
                { key: "upload", label: "Upload", icon: Upload },
                { key: "record", label: "Gravar", icon: Video },
              ].map(t => {
                const Icon = t.icon; const active = videoTab === t.key;
                return (
                  <button key={t.key} onClick={()=>setVideoTab(t.key)} data-testid={`video-tab-${t.key}`}
                    className={`px-4 py-2.5 text-sm font-bold uppercase tracking-wider transition-all border-b-2 flex items-center gap-1.5 ${active ? "text-rose-600 border-rose-600" : "text-slate-500 border-transparent hover:text-slate-900"}`}>
                    <Icon className="w-4 h-4"/>{t.label}
                  </button>
                );
              })}
            </div>

            {videoTab === "library" && (
              <div>
                <div className="relative mb-3">
                  <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                  <Input value={librarySearch} onChange={(e)=>setLibrarySearch(e.target.value)} placeholder="Buscar exercício ou músculo (peito, costas...)" className="pl-9" data-testid="video-library-search"/>
                </div>
                <div className="grid md:grid-cols-2 gap-2 max-h-[450px] overflow-y-auto">
                  {library
                    .filter(ex => !librarySearch || ex.name.toLowerCase().includes(librarySearch.toLowerCase()) || ex.muscle.toLowerCase().includes(librarySearch.toLowerCase()))
                    .map((ex, i) => (
                      <button key={i} onClick={()=>{ updateExercise(videoPickerTarget.di, videoPickerTarget.ei, "name", ex.name); setExerciseVideo(ex.video_url); }} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-rose-300 hover:bg-rose-50 text-left transition-all" data-testid={`library-ex-${i}`}>
                        <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center shrink-0"><Play className="w-4 h-4 text-rose-600"/></div>
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-slate-900 text-sm">{ex.name}</div>
                          <div className="text-xs text-slate-500">{ex.muscle}</div>
                        </div>
                      </button>
                    ))}
                </div>
              </div>
            )}

            {videoTab === "youtube" && (
              <div className="space-y-3">
                <p className="text-sm text-slate-500">Cole a URL de qualquer vídeo do YouTube (watch?v=... ou youtu.be/...):</p>
                <Input value={ytUrl} onChange={(e)=>setYtUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=..." data-testid="video-youtube-input"/>
                {ytToEmbed(ytUrl) && (
                  <div className="aspect-video rounded-lg overflow-hidden bg-black">
                    <iframe src={ytToEmbed(ytUrl)} title="preview" className="w-full h-full"/>
                  </div>
                )}
                <Button onClick={applyYouTube} className="w-full bg-rose-600 hover:bg-rose-700 font-bold uppercase" data-testid="video-youtube-apply"><Youtube className="w-4 h-4 mr-2"/>Anexar vídeo do YouTube</Button>
              </div>
            )}

            {videoTab === "upload" && (
              <div className="space-y-3">
                <div className="rounded-xl border-2 border-dashed border-slate-300 p-8 text-center hover:border-rose-400 transition-colors">
                  <Upload className="w-12 h-12 text-slate-300 mx-auto mb-2"/>
                  <p className="text-sm text-slate-600 mb-3">Selecione um vídeo do computador (MP4, WebM, MOV — máx 30MB)</p>
                  <input type="file" accept="video/*" onChange={(e)=>uploadVideo(e.target.files?.[0])} className="hidden" id="video-upload-input" data-testid="video-upload-input"/>
                  <Button asChild className="bg-rose-600 hover:bg-rose-700 font-bold uppercase">
                    <label htmlFor="video-upload-input" className="cursor-pointer"><Upload className="w-4 h-4 mr-2"/>Escolher arquivo</label>
                  </Button>
                </div>
                <p className="text-xs text-slate-400">O vídeo é salvo diretamente no banco (base64). Para vídeos maiores, use a aba YouTube ou hospede em um CDN.</p>
              </div>
            )}

            {videoTab === "record" && (
              <RecordVideoPanel onDone={setExerciseVideo}/>
            )}
          </DialogContent>
        </Dialog>

        {/* Workout AI */}
        <Dialog open={wkAIOpen} onOpenChange={setWkAIOpen}>
          <DialogContent className="max-w-lg" data-testid="wk-ai-dialog">
            <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2 mb-1"><Sparkles className="w-5 h-5 text-sky-600"/>Gerar treino com IA</h3>
            <p className="text-sm text-slate-500 mb-4">GPT-5.4 monta a ficha personalizada para {s.name.split(" ")[0]} baseada no nível ({s.experience_level || "iniciante"}) e no objetivo abaixo.</p>
            <div className="space-y-3">
              <div><Label>Objetivo *</Label><Input value={wkAIForm.goal} onChange={(e)=>setWkAIForm({...wkAIForm,goal:e.target.value})} placeholder="Ex: hipertrofia peito e costas" data-testid="wk-ai-goal"/></div>
              <div>
                <Label>Dias por semana</Label>
                <Select value={String(wkAIForm.days_per_week)} onValueChange={(v)=>setWkAIForm({...wkAIForm,days_per_week:parseInt(v)})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">2 dias (A/B)</SelectItem>
                    <SelectItem value="3">3 dias (A/B/C)</SelectItem>
                    <SelectItem value="4">4 dias (A/B/C/D)</SelectItem>
                    <SelectItem value="5">5 dias (A/B/C/D/E)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Focos (opcional)</Label><Input value={wkAIForm.focus_areas} onChange={(e)=>setWkAIForm({...wkAIForm,focus_areas:e.target.value})} placeholder="Ex: peito, costas, glúteos"/></div>
              <div><Label>Observações (opcional)</Label><Textarea rows={2} value={wkAIForm.notes} onChange={(e)=>setWkAIForm({...wkAIForm,notes:e.target.value})} placeholder="Restrições, preferências, lesões..."/></div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={()=>setWkAIOpen(false)}>Cancelar</Button>
              <Button onClick={generateWkAI} disabled={wkGenerating} className="bg-slate-900 hover:bg-black text-white font-bold uppercase" data-testid="wk-ai-generate">
                {wkGenerating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Gerando...</> : <><Sparkles className="w-4 h-4 mr-2"/>Gerar treino</>}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 hover:border-slate-300 transition-colors">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: color + "1a" }}>
          <Icon className="w-4 h-4" style={{ color }}/>
        </div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      </div>
      <div className="font-heading text-xl font-bold text-slate-900 mt-2 truncate">{value}</div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div className="text-sm text-slate-900 font-medium">{value || "—"}</div>
    </div>
  );
}

function RecordVideoPanel({ onDone }) {
  const [recording, setRecording] = React.useState(false);
  const [previewUrl, setPreviewUrl] = React.useState(null);
  const videoRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const recorderRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  React.useEffect(() => () => { streamRef.current?.getTracks().forEach(t=>t.stop()); }, []);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "user" }, width: { ideal: 640 }, height: { ideal: 480 } },
        audio: true,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        videoRef.current.setAttribute("playsinline", "true");
        try { await videoRef.current.play(); } catch {}
      }
      chunksRef.current = [];
      const rec = new MediaRecorder(stream, { mimeType: "video/webm" });
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        const reader = new FileReader();
        reader.onload = () => { setPreviewUrl(reader.result); toast.success("Gravação concluída — clique em Anexar"); };
        reader.readAsDataURL(blob);
        streamRef.current?.getTracks().forEach(t=>t.stop());
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
      setTimeout(() => { if (rec.state === "recording") { rec.stop(); setRecording(false); } }, 30000);
    } catch { toast.error("Sem acesso à câmera/microfone"); }
  };
  const stopRec = () => {
    if (recorderRef.current?.state === "recording") { recorderRef.current.stop(); setRecording(false); }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">Grave um vídeo curto (até 30s) direto pela câmera para correção postural personalizada do aluno.</p>
      <div className="relative bg-black rounded-xl overflow-hidden aspect-video">
        <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover"/>
        {recording && (
          <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-rose-600 text-white text-xs font-bold">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse"/>REC · Máx 30s
          </div>
        )}
      </div>
      {!recording ? (
        <Button onClick={startRec} className="w-full bg-rose-600 hover:bg-rose-700 font-bold uppercase" data-testid="video-record-start"><Camera className="w-4 h-4 mr-2"/>Iniciar gravação</Button>
      ) : (
        <Button onClick={stopRec} className="w-full bg-slate-900 hover:bg-black font-bold uppercase" data-testid="video-record-stop">Parar gravação</Button>
      )}
      {previewUrl && !recording && (
        <div className="space-y-2">
          <video src={previewUrl} controls className="w-full rounded-lg bg-black" style={{maxHeight: 240}}/>
          <div className="flex gap-2">
            <Button onClick={()=>setPreviewUrl(null)} variant="outline" className="flex-1">Refazer</Button>
            <Button onClick={()=>onDone(previewUrl)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 font-bold uppercase" data-testid="video-record-attach">Anexar ao exercício</Button>
          </div>
        </div>
      )}
    </div>
  );
}
